// src/pages/ChatPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../context/AuthContext";
import { usePoints } from "../context/PointsContext";
import { buildCoachingState } from "../utils/buildCoachingState";
import {
  buildStructuredFallbackSummary,
  createSessionFromThread,
  formatSummarySnippetForAudience,
  formatSupportTextForAudience,
  formatSummaryTextForAudience,
  normalizeSessionRecordSummary,
  normalizeStructuredSummary,
} from "../utils/summary";
import ScheduleFollowUpModal from "../components/ScheduleFollowUpModal";
import EmailSummaryModal from "../components/EmailSummaryModal";
import CoachNotesPanel from "../components/CoachNotesPanel";
import SessionFollowUpPanel from "../components/SessionFollowUpPanel";
import FocusedChatSurface from "../components/chat/FocusedChatSurface";
import ChatDetailsPanel from "../components/chat/ChatDetailsPanel";
import useLiveSessionRecord, {
  hasMatchingAuthoritativeCompletion,
  selectSessionRecordForView,
} from "../hooks/useLiveSessionRecord";
import { logOrgActivity } from "../utils/orgAudit";
import { NIA_GAMES as GAMES } from "../utils/games"; // ✅ use server-approved catalogue
import Card from "../ui/Card";
import { db, functionsClient } from "../firebase";
import {
  confirmOwnedSessionReadableFromServer,
  filterCachedSessionsForViewer,
  writeSessionCompletionToFirestore,
  writeSessionFieldsToFirestore,
  writeSessionToFirestore,
} from "../utils/sessionSync";
import { fetchOrgSessionById, fetchSameUserOrgSessions } from "../utils/orgData";
import { detectSessionFlag, hasActionableSessionFlagSuggestion } from "../utils/sessionFlags";
import {
  detectCumulativeSafetyAdjacentSignal,
  getSafetyContextState,
} from "../utils/safetyContext";
import {
  getCoachThreadLastRead,
  getCoachThreadReadKey,
  getSessionCoachThreadMessages,
  listenCoachThread,
  markCoachThreadRead,
  sendCoachThreadMessage,
} from "../utils/coachThread";
import { notifyCoachForOrg } from "../utils/notifyCoach";
import { getProfile } from "../utils/roles";
import { firstUserMessageMetadata, getSessionSortFieldUsed, sortSessionsByRecency } from "../utils/sessionOrdering";
import {
  buildSameUserContinuityPacket,
  getChatExitTarget,
  shouldRetrieveSameUserContinuity,
} from "../utils/sessionContinuity";
import {
  getSessionScopedValue,
  resolveActiveSessionSummary,
  resolveCurrentRecordSummary,
  resolveDisplaySessionSummary,
  sessionKey,
  sessionFollowUpFingerprint,
  sessionMessageFingerprint,
  withSessionScope,
} from "../utils/sessionScope";
import {
  claimFlagCase,
  createFlagCaseFromSession,
  createSessionInterventionCaseFromSession,
  fetchCoachInterventionCaseBySession,
  getFlagCaseStatusLabel,
  isFinalFlagStatus,
  recordFlagCaseCoachContactBySession,
  resolveFlagCase,
} from "../utils/flagCases";
import {
  buildSessionFollowUp,
  buildSessionFollowUpRecommendation,
  completeOrdinaryFollowUpIfContinued,
  getDefaultFollowUpReminderAt,
  isCoachActionRequiredFollowUp,
  getFollowUpDisplayStatus,
  getFollowUpState,
  formatFollowUpDate,
  mergeSessionFollowUp,
  retainSupersededFollowUpHistory,
  resolveDisplaySessionFollowUp,
  supersedeStaleSuggestedFollowUp,
  toFollowUpDateInputValue,
  toFollowUpTimeInputValue,
  toScheduledFollowUpIso,
} from "../utils/followUp";
import {
  getAnonymousSessionLabel,
  getCoachThreadSenderLabel,
  getRenderedSessionMessage,
  getSessionDisplayText,
  getSessionDisplayName,
  getSessionPrivacyStatus,
  getViewerSessionDisplayName,
  isOwnSession,
  getSupportConsentStatus,
  normalizeSupportConsent,
  normalizeSessionPrivacy,
} from "../utils/sessionPrivacy";
import {
  canDisplayPostSessionFinalSummaryDetails,
  getPostSessionAutomaticEmailLabel,
  getPostSessionFinalizationDisplayState,
  getPostSessionFinalizationLabel,
  getPostSessionManualResendEligibility,
  getPostSessionManualResendLabel,
  getPostSessionReminderLabel,
  normalizePostSessionContinuation,
  normalizeSummaryDelivery,
} from "../utils/summaryDelivery";
import { mapSessionMessagesForDisplay } from "../utils/sessionMessages";
import { retainSupersededSummaryHistory } from "../utils/summaryHistory";
import { stampSummarySourceVersion } from "../utils/transcriptVersion";
import {
  buildSessionCompletionVersion,
  canCreateOwnedCompletion,
  getSessionLifecycleLabel,
} from "../utils/sessionCompletion";
import {
  describeRuntimeSessionRecord,
  recordRuntimeDiagnostic,
  registerRuntimeTestAction,
  runRuntimeTestHook,
} from "../utils/runtimeDiagnostics";
import {
  resolveCompletionAssistantText,
} from "../utils/completionReply";
import {
  getTrustedActiveSummaryState,
  hasTrustedFinalSummaryProvenance,
  validatePostSessionProvenance,
} from "../utils/postSessionProvenance";
import { resolveRecordSummaryForView } from "../utils/recordSummaryProjection";
import {
  buildHydratedSessionTarget,
  readMarkedPostSessionReturn,
} from "../utils/authReturnTarget";
import {
  SESSION_ORIGINS,
  createSessionListenerLifecycle,
  isSessionListenerGateOpen,
  shouldStartInitialParentWrite,
  transitionSessionListenerLifecycle,
} from "../utils/sessionListenerLifecycle";

const operationalIntent = require("../utils/operationalIntent");
const {
  applyCanonicalOperationalIntent,
  buildOperationalSupportResponse,
} = operationalIntent;
const GLOBAL_SESS_KEY = "nia_sessions";
const HISTORY_DEFAULT_VISIBLE_COUNT = 6;
const SESSION_REMOTE_WRITE = Symbol("sessionRemoteWrite");
const orgKey = (orgId) => `${GLOBAL_SESS_KEY}__org_${orgId || "org_default"}`;
const userKey = (uid, orgId) => (uid ? `${orgKey(orgId)}__uid_${uid}` : "");
const SHOW_TS = () => localStorage.getItem("pref_timestamps") === "true";

function resolutionSelectionForCase(row) {
  const status = String(row?.status || "").trim().toLowerCase();
  const resolutionType = String(row?.resolutionType || "").trim().toLowerCase();
  if (resolutionType === "false_positive") return "false_positive";
  if (["resolved_no_action", "no_intervention_needed", "reviewed_no_action"].includes(status) || resolutionType === "reviewed_no_action") return "no_intervention_needed";
  if (status === "intervened" || resolutionType === "intervened") return "intervened";
  if (status === "resolved" || resolutionType === "resolved") return "resolved";
  return "resolved";
}

const SUMMARY_AUDIT_FIELDS = [
  "generationStatus",
  "status",
  "isCurrent",
  "sourceMessageCount",
  "sourceLastMessageAt",
  "sourceLatestMessageAt",
  "sourceTranscriptFingerprint",
  "sourceFollowUpFingerprint",
  "sourceTopic",
  "staleReason",
  "staleDetectedAt",
  "supersededAt",
  "supersededReason",
  "persistenceStatus",
  "persistedAt",
  "persistenceFailedAt",
  "persistenceError",
  "persistenceUpdatedAt",
];

function preserveSummaryAuditFields(normalizedSummary, sourceSummary) {
  if (!normalizedSummary || typeof normalizedSummary !== "object") return normalizedSummary;
  if (!sourceSummary || typeof sourceSummary !== "object") return normalizedSummary;
  return SUMMARY_AUDIT_FIELDS.reduce((result, field) => (
    Object.prototype.hasOwnProperty.call(sourceSummary, field)
      ? { ...result, [field]: sourceSummary[field] }
      : result
  ), normalizedSummary);
}

function getSummaryPersistenceLabel(summary = {}) {
  const status = String(summary?.persistenceStatus || "").trim().toLowerCase();
  if (status === "persisted") return "Saved to session";
  if (status === "pending") return "Saving to session";
  if (status === "failed") return "Session save failed";
  return "";
}

function buildSessionPersistenceFailureSummary(summary = {}, id = "") {
  const failedAt = new Date().toISOString();
  const unavailableText = "Summary unavailable because this session could not be saved.";
  return withSessionScope({
    ...summary,
    summaryText: unavailableText,
    summarySnippet: unavailableText,
    detectedThemes: [],
    suggestedNextStep: "",
    nextFollowUpFocus: "",
    generationStatus: "failed",
    status: "failed",
    source: "session-persistence-failure",
    sourceTopic: "",
    fallbackUsed: false,
    persistenceStatus: "failed",
    persistedAt: null,
    persistenceFailedAt: failedAt,
    persistenceError:
      "The session transcript could not be saved to the shared session record. No generated recap is being shown as authoritative.",
    persistenceUpdatedAt: failedAt,
  }, id);
}

function normalizeRecord(record, fallbackIdentity = {}) {
  const fallbackName =
    typeof fallbackIdentity === "string"
      ? fallbackIdentity
      : String(fallbackIdentity?.name || "").trim();
  const created = record.createdAt || record.date || Date.now();
  const id = record.id || created;
  const trustedActiveSummary = getTrustedActiveSummaryState(record);
  const normalizedSummary = (
    hasTrustedFinalSummaryProvenance(record) ||
    trustedActiveSummary.snapshotValid
  )
    ? record.sessionSummary
    : preserveSummaryAuditFields(
        normalizeSessionRecordSummary(record),
        record.sessionSummary
      );
  const scopedSummary = sessionKey(record.sessionSummary?.sessionId || normalizedSummary?.sessionId)
    ? withSessionScope(normalizedSummary, id)
    : normalizedSummary;
  const scopedFollowUp = withSessionScope(record.followUp, id);
  const scopedFollowUpRecommendation = withSessionScope(record.followUpRecommendation, id);
  const rawSummaryText = String(record?.sessionSummary?.summaryText || "").trim();
  const normalizedSummaryText = String(scopedSummary?.summaryText || "").trim();
  const summaryEvidence = rawSummaryText && rawSummaryText !== normalizedSummaryText
    ? retainSupersededSummaryHistory(
        {
          sessionSummary: scopedSummary,
          summaryHistory: Array.isArray(record.summaryHistory) ? record.summaryHistory : [],
        },
        {
          sessionSummary: record.sessionSummary,
          summaryHistory: Array.isArray(record.summaryHistory) ? record.summaryHistory : [],
        }
      )
    : {
        summaryHistory: Array.isArray(record.summaryHistory) ? record.summaryHistory : [],
      };
  return {
    ...record,
    id,
    createdAt: created,
    updatedAt: record.updatedAt || null,
    lastMessageAt: record.lastMessageAt || created,
    user: record.user || record.client || fallbackName || "Unknown",
    client: record.client || record.user || fallbackName || "Unknown",
    userId: record.userId || fallbackIdentity?.userId || "",
    userEmail: record.userEmail || fallbackIdentity?.userEmail || "",
    startedAt: record.startedAt || created,
    endedAt: record.endedAt || null,
    messageCount: Number(record.messageCount || (Array.isArray(record.messages) ? record.messages.length : 0)),
    sessionSummary: scopedSummary,
    summaryDelivery: normalizeSummaryDelivery(record.summaryDelivery),
    summaryHistory: summaryEvidence.summaryHistory,
    followUpHistory: Array.isArray(record.followUpHistory) ? record.followUpHistory : [],
    followUp: scopedFollowUp,
    followUpRecommendation: scopedFollowUpRecommendation,
    followUpCreated: Boolean(record.followUpCreated || record.followUp),
  };
}

function completionStateFromRecord(record = {}) {
  return {
    sessionComplete: record?.sessionComplete === true,
    completionReason: String(record?.completionReason || ""),
    completionAction: String(record?.completionAction || ""),
    completionVersion:
      record?.completionVersion && typeof record.completionVersion === "object"
        ? record.completionVersion
        : null,
    endedAt: record?.endedAt || null,
  };
}

function completionPersistenceFromRecord(record = {}, confirmed = false) {
  const versionId = String(record?.completionVersion?.id || "");
  if (!versionId || record?.sessionComplete !== true) {
    return { status: "idle", versionId: "", lastError: "" };
  }
  return {
    status: confirmed ? "persisted" : "unverified",
    versionId,
    lastError: "",
  };
}

function hasTrustedCompletedRecord(record = {}) {
  return validatePostSessionProvenance(record).completionValid;
}

function sortNewest(arr) {
  return sortSessionsByRecency(arr);
}

function stripMarkdownFormatting(text = "") {
  return String(text || "")
    .replace(/\r\n?/g, "\n")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/_([^_\n]+)_/g, "$1")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function getLocalFirstName() {
  try {
    return String(getProfile()?.firstName || "").trim();
  } catch {
    return "";
  }
}

function buildUserLocationContext(user = {}) {
  const country = String(user?.country || user?.countryCode || "").trim();
  const province = String(user?.province || user?.state || user?.region || "").trim();
  const city = String(user?.city || user?.town || user?.locality || "").trim();
  return { country, province, city };
}

export default function ChatPage() {
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  const { user, activeOrgId, setActiveOrgId } = useAuth();
  const role = user?.role || "member";
  const canUseCoachTools = role === "coach" || role === "superadmin";
  const canReviewOrgSessions = ["coach", "admin", "superadmin", "investor"].includes(role);
  const canUseChat = role === "member" || role === "coach" || role === "admin" || role === "superadmin" || role === "investor";
  const canSeeMemberSafeSummary = role === "member" || canUseCoachTools || role === "investor";
  const isReadOnly = role === "investor";

  const username = user?.username || "Unknown";
  const localFirstName = useMemo(() => getLocalFirstName(), []);
  const displayName = (user?.firstName || "").trim() || localFirstName || username || "there";
  const memberIdentity = useMemo(
    () => ({
      client: displayName,
      user: displayName,
      userEmail: user?.email || "",
      userId: user?.uid || "",
    }),
    [displayName, user?.email, user?.uid]
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resumeFollowUp = searchParams.get("resume") === "followup";
  const postSessionReturnTarget = useMemo(
    () => readMarkedPostSessionReturn(searchParams),
    [searchParams]
  );

  const pointsApi = usePoints?.() || {};
  const addPoints =
    pointsApi.addPoints ||
    ((n) => {
      const cur = Number(localStorage.getItem("nia_points") || 0);
      localStorage.setItem("nia_points", String(cur + (n || 0)));
    });
  const incMessage =
    pointsApi.incMessage ||
    (() => {
      const cur = Number(localStorage.getItem("nia_messages") || 0);
      localStorage.setItem("nia_messages", String(cur + 1));
    });

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Current session identity (stable across the whole conversation)
  const [sessionId, setSessionId] = useState(() => {
    const fromUrl = searchParams.get("id");
    return fromUrl ? String(fromUrl) : null;
  });
  const [sessionListenerLifecycle, setSessionListenerLifecycle] = useState(
    () => createSessionListenerLifecycle()
  );
  const sessionListenerLifecycleRef = useRef(sessionListenerLifecycle);
  const pendingSessionOriginRef = useRef({ sessionId: "", origin: "" });
  const initialParentWriteCounterRef = useRef(0);
  const initialParentWritePromiseRef = useRef({
    scopeKey: "",
    operationId: "",
    promise: null,
  });
  const transitionSessionListener = useCallback((event) => {
    const previous = sessionListenerLifecycleRef.current;
    const next = transitionSessionListenerLifecycle(previous, event);
    if (next !== previous) {
      sessionListenerLifecycleRef.current = next;
      setSessionListenerLifecycle(next);
      recordRuntimeDiagnostic("chat.session-listener-transition", {
        eventType: String(event?.type || ""),
        sessionOrigin: next.origin,
        persistenceState: next.state,
        selectedSessionId: next.sessionId,
        selectedSessionIdType: typeof event?.sessionId,
        scopeKey: next.scopeKey,
        initialWriteOperationId: next.initialWriteOperationId,
        initialWriteKind: next.initialWriteKind,
        documentReady: next.documentReady,
        listenerAttempt: next.listenerAttempt,
        recoveryAttempted: next.recoveryAttempted,
      });
    }
    return next;
  }, []);
  // Remember the original "createdAt" for this session so it doesn't jump
  const sessionStartRef = useRef(null);

  // Extra panes
  const [notes, setNotes] = useState({ observations: "", reframes: "", next: "" });
  const [attachments, setAttachments] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [showSchedule, setShowSchedule] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingFeedback, setRatingFeedback] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingDismissed, setRatingDismissed] = useState(false);
  const [ratingData, setRatingData] = useState(null);
  const [coachThread, setCoachThread] = useState([]);
  const [coachInput, setCoachInput] = useState("");
  const [coachSending, setCoachSending] = useState(false);
  const [showCoachPanel, setShowCoachPanel] = useState(false);
  const [dismissedCoachMessageAt, setDismissedCoachMessageAt] = useState(0);
  const [activeInterventionCase, setActiveInterventionCase] = useState(null);
  const [flagCaseBusy, setFlagCaseBusy] = useState(false);
  const [flagCaseStatus, setFlagCaseStatus] = useState("");
  const [resolutionType, setResolutionType] = useState("resolved");
  const [sessionSummary, setSessionSummary] = useState(null);
  const [summaryDelivery, setSummaryDelivery] = useState(() => normalizeSummaryDelivery(null));
  const [postSessionContinuation, setPostSessionContinuation] = useState(null);
  const [sessionCompletion, setSessionCompletion] = useState(() => ({
    sessionComplete: false,
    completionReason: "",
    completionAction: "",
    completionVersion: null,
    endedAt: null,
  }));
  const [returnSignalError, setReturnSignalError] = useState("");
  const [returnSignalRetryNonce, setReturnSignalRetryNonce] = useState(0);
  const [completionPersistence, setCompletionPersistence] = useState(() => ({
    status: "idle",
    versionId: "",
    lastError: "",
  }));
  const completedSessionLocked = sessionCompletion.sessionComplete === true;
  const versionedCompletionLocked = Boolean(
    completedSessionLocked && sessionCompletion.completionVersion?.id
  );
  const [followUp, setFollowUp] = useState(null);
  const [followUpRecommendation, setFollowUpRecommendation] = useState(null);
  const [continuationOffered, setContinuationOffered] = useState(false);
  const [sessionPrivacy, setSessionPrivacy] = useState(() => normalizeSessionPrivacy(null));
  const [sessionSupportConsent, setSessionSupportConsent] = useState(() => normalizeSupportConsent(null));
  const [sessionIdentity, setSessionIdentity] = useState(() => memberIdentity);
  const canPersistOwnedCompletion = canCreateOwnedCompletion({
    role,
    viewerUserId: user?.uid || "",
    sessionUserId: sessionIdentity.userId || (
      role === "member" ? user?.uid || "" : ""
    ),
  });
  const [animatedAssistantMessageId, setAnimatedAssistantMessageId] = useState(null);

  const chatBoxRef = useRef(null);
  const chatViewportRef = useRef(null);
  const latestMessagesRef = useRef([]);
  const postSessionReturnAttemptRef = useRef("");
  const mountedRef = useRef(true);
  const coachInterruptHydratedRef = useRef(false);
  const latestCoachTimestampRef = useRef(0);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);
  useEffect(() => registerRuntimeTestAction(
    "selectOrganisation",
    (nextOrgId) => setActiveOrgId(String(nextOrgId || ""))
  ), [setActiveOrgId]);
  useEffect(() => {
    const el = chatBoxRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, loading]);
  useEffect(() => {
    latestMessagesRef.current = messages;
  }, [messages]);
  const scrollToChatViewport = useCallback(() => {
    if (typeof window === "undefined" || window.innerWidth > 960) return;
    const target = chatViewportRef.current;
    if (!target) return;
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);
  const matchesSessionId = useCallback((left, right) => {
    return String(left || "").trim() !== "" && String(left || "") === String(right || "");
  }, []);

  // ----- LocalStorage helpers -----
  const orgId =
    postSessionReturnTarget?.orgId ||
    activeOrgId ||
    user?.orgId ||
    "org_default";
  const selectedSessionDocumentReady = isSessionListenerGateOpen(
    sessionListenerLifecycle,
    { orgId, sessionId }
  );
  const handleLiveSessionListenerEvent = useCallback((event) => {
    const next = transitionSessionListener(event);
    if (
      event?.type === "listener_failed" &&
      next.origin === SESSION_ORIGINS.NEW &&
      next.documentReady &&
      !next.recoveryAttempted
    ) {
      transitionSessionListener({
        type: "retry_listener",
        orgId: event.orgId,
        sessionId: event.sessionId,
        at: new Date().toISOString(),
      });
    }
  }, [transitionSessionListener]);
  const { record: liveSessionRecord } = useLiveSessionRecord({
    db,
    authScopeKey: user?.uid || "",
    orgId,
    sessionId,
    enabled: Boolean(user?.uid && selectedSessionDocumentReady),
    sessionOrigin: sessionListenerLifecycle.origin,
    persistenceState: sessionListenerLifecycle.state,
    listenerAttempt: sessionListenerLifecycle.listenerAttempt,
    onListenerEvent: handleLiveSessionListenerEvent,
    reconcileAfterCompletion: Boolean(
      completedSessionLocked && selectedSessionDocumentReady
    ),
  });
  const selectedLiveSessionRecord = useMemo(() => {
    const selected = selectSessionRecordForView({
      liveRecord: liveSessionRecord,
      sessionId,
    });
    if (!selected) return null;
    return normalizeRecord(selected, {
      name: username,
      userId: role === "member" ? user?.uid || "" : "",
      userEmail: user?.email || "",
    });
  }, [liveSessionRecord, role, sessionId, user?.email, user?.uid, username]);
  const liveSessionRecordRef = useRef(null);
  liveSessionRecordRef.current = selectedLiveSessionRecord;

  useEffect(() => {
    recordRuntimeDiagnostic("chat.live-record-selected", {
      renderCount: renderCountRef.current,
      selectedSessionId: sessionId,
      selectedSessionIdType: typeof sessionId,
      liveSessionRecord: describeRuntimeSessionRecord(liveSessionRecord),
      selectedLiveSessionRecord: describeRuntimeSessionRecord(
        selectedLiveSessionRecord
      ),
    });
  }, [liveSessionRecord, selectedLiveSessionRecord, sessionId]);

  useEffect(() => {
    if (
      !postSessionReturnTarget ||
      !user?.uid ||
      role !== "member"
    ) {
      return;
    }
    const { orgId: returnOrgId, sessionId: returnSessionId } =
      postSessionReturnTarget;
    const attemptKey = `${returnOrgId}:${returnSessionId}`;
    if (postSessionReturnAttemptRef.current === attemptKey) return;
    postSessionReturnAttemptRef.current = attemptKey;
    let active = true;
    setReturnSignalError("");

    const recordReturn = httpsCallable(functionsClient, "recordSessionReturn");
    recordReturn({
      orgId: returnOrgId,
      sessionId: returnSessionId,
    })
      .then(() => {
        if (!active || !mountedRef.current) return;
        pendingSessionOriginRef.current = {
          sessionId: String(returnSessionId),
          origin: SESSION_ORIGINS.CURRENT,
        };
        transitionSessionListener({
          type: "select_existing",
          origin: SESSION_ORIGINS.CURRENT,
          orgId: returnOrgId,
          sessionId: returnSessionId,
        });
        setActiveOrgId(returnOrgId);
        setReturnSignalError("");
        navigate(buildHydratedSessionTarget({
          orgId: returnOrgId,
          sessionId: returnSessionId,
        }), { replace: true });
      })
      .catch(() => {
        if (!active || !mountedRef.current) return;
        postSessionReturnAttemptRef.current = "";
        setReturnSignalError(
          "Nia could not safely record this authenticated return. Retry the secure return before continuing."
        );
      });

    return () => {
      active = false;
    };
  }, [
    navigate,
    postSessionReturnTarget,
    returnSignalRetryNonce,
    role,
    setActiveOrgId,
    user?.uid,
    transitionSessionListener,
  ]);

  const getUserSessions = useCallback(() => {
    try {
      const storageKey = canReviewOrgSessions ? orgKey(orgId) : userKey(user?.uid, orgId);
      if (!storageKey) return [];
      const arr = JSON.parse(localStorage.getItem(storageKey) || "[]");
      const visible = filterCachedSessionsForViewer(arr, user);
      return sortNewest(visible.map((r) => normalizeRecord(r, {
        name: username,
        userId: user?.uid || "",
        userEmail: user?.email || "",
      })));
    } catch {
      return [];
    }
  }, [canReviewOrgSessions, username, orgId, user]);
  const upsertRecord = (bucketKey, record) => {
    const list = JSON.parse(localStorage.getItem(bucketKey) || "[]");
    const idx = list.findIndex((r) => String(r.id) === String(record.id));
    if (idx >= 0) {
      list[idx] = record; // replace
    } else {
      list.unshift(record); // insert newest first
    }
    localStorage.setItem(bucketKey, JSON.stringify(list));
  };
  const findById = useCallback((id) => {
    const perUser = getUserSessions();
    const rec = perUser.find((r) => String(r.id) === String(id));
    return rec ? { rec, source: canReviewOrgSessions ? "org" : "user" } : null;
  }, [canReviewOrgSessions, getUserSessions]);

  const showDefaultGreeting = useCallback(() => {
    const greeting = [
      {
        id: Date.now(),
        sender: "NIA",
        text: "Hi, I’m Nia — your cognitive support coach. How can I support you today?",
        createdAt: new Date().toISOString(),
      },
    ];
    latestMessagesRef.current = greeting;
    setMessages(greeting);
    setSessionSummary(null);
    setSummaryDelivery(normalizeSummaryDelivery(null));
    setPostSessionContinuation(null);
    setReturnSignalError("");
    setSessionCompletion({
      sessionComplete: false,
      completionReason: "",
      completionAction: "",
      completionVersion: null,
      endedAt: null,
    });
    setCompletionPersistence(completionPersistenceFromRecord());
    setFollowUp(null);
    setFollowUpRecommendation(null);
    setContinuationOffered(false);
    setSessionPrivacy(normalizeSessionPrivacy(null));
    setSessionSupportConsent(normalizeSupportConsent(null));
    setSessionIdentity(memberIdentity);
  }, [memberIdentity]);

  const toThreadMessages = useCallback(
    (threadMessages = []) =>
      threadMessages.map((m) => ({
        role: m.sender === "NIA" ? "assistant" : "user",
        content: m.text,
        createdAt: m.createdAt || undefined,
      })),
    []
  );

  const recordMessagesForSummary = useCallback((record = {}) => {
    return (Array.isArray(record?.messages) ? record.messages : [])
      .map((message) => ({
        role: String(message?.role || "").toLowerCase() === "assistant" || message?.sender === "NIA" ? "assistant" : "user",
        content: message?.content || message?.text || "",
        createdAt: message?.createdAt || message?.sentAt || message?.timestamp || undefined,
      }))
      .filter((message) => String(message.content || "").trim());
  }, []);

  const persistSessionRecord = useCallback((record, options = {}) => {
    const priorRecord = findById(record?.id)?.rec || null;
    const withHistory = retainSupersededSummaryHistory(record, priorRecord);
    const withFollowUpHistory = retainSupersededFollowUpHistory(withHistory, priorRecord);
    const normalized = normalizeRecord(withFollowUpHistory, {
      name: username,
      userId: user?.uid || "",
      userEmail: user?.email || "",
    });
    const storageKey = canReviewOrgSessions ? orgKey(orgId) : userKey(user?.uid, orgId);
    if (!storageKey) return null;
    upsertRecord(storageKey, normalized);
    recordRuntimeDiagnostic("chat.local-history-upsert", {
      selectedSessionId: String(normalized.id || ""),
      selectedSessionIdType: typeof normalized.id,
      sessionOrigin: sessionListenerLifecycleRef.current.origin,
      persistenceState: sessionListenerLifecycleRef.current.state,
      documentReady: sessionListenerLifecycleRef.current.documentReady,
    });
    try {
      console.info("[sessions] sessionPersisted", {
        sessionListSource: "chat_persist",
        sessionId: String(normalized.id || ""),
        createdAt: normalized.createdAt || 0,
        updatedAt: normalized.updatedAt || 0,
        lastMessageAt: normalized.lastMessageAt || 0,
        messageCount: Number(normalized.messageCount || normalized.messages?.length || 0),
        sessionSummaryCreatedAt: normalized.sessionSummary?.generatedAt || normalized.sessionSummary?.createdAt || 0,
        dashboardListName: "chat_session_record",
        sortFieldUsed: getSessionSortFieldUsed(normalized),
        ...firstUserMessageMetadata(normalized),
      });
    } catch {}
    if (!options?.skipRemote) {
      const generalSessionRecord = { ...normalized };
      delete generalSessionRecord.summaryHistory;
      delete generalSessionRecord.followUpHistory;
      delete generalSessionRecord.summaryDelivery;
      delete generalSessionRecord.supportReview;

      const auditPatch = {};
      if (
        JSON.stringify(normalized.summaryHistory || []) !==
        JSON.stringify(priorRecord?.summaryHistory || [])
      ) {
        auditPatch.summaryHistory = normalized.summaryHistory || [];
      }
      if (
        JSON.stringify(normalized.followUpHistory || []) !==
        JSON.stringify(priorRecord?.followUpHistory || [])
      ) {
        auditPatch.followUpHistory = normalized.followUpHistory || [];
      }
      (Array.isArray(options?.auditFields) ? options.auditFields : [])
        .forEach((field) => {
          if (Object.prototype.hasOwnProperty.call(normalized, field)) {
            auditPatch[field] = normalized[field];
          }
        });

      let initialWriteOperationId = "";
      const writeScopeKey = `${orgId}:${String(normalized.id || "")}`;
      const remoteWrite = (async () => {
        const pendingInitialWrite = initialParentWritePromiseRef.current;
        if (
          sessionListenerLifecycleRef.current.scopeKey === writeScopeKey &&
          sessionListenerLifecycleRef.current.state ===
            "initial_parent_write_in_progress" &&
          pendingInitialWrite.scopeKey === writeScopeKey &&
          pendingInitialWrite.promise
        ) {
          await pendingInitialWrite.promise.catch(() => false);
        }

        if (shouldStartInitialParentWrite(
          sessionListenerLifecycleRef.current,
          { orgId, sessionId: normalized.id }
        )) {
          initialParentWriteCounterRef.current += 1;
          initialWriteOperationId = [
            "initial-parent-set",
            writeScopeKey,
            initialParentWriteCounterRef.current,
          ].join(":");
          transitionSessionListener({
            type: "initial_write_started",
            orgId,
            sessionId: normalized.id,
            operationId: initialWriteOperationId,
            writeKind: "active_session_set_doc",
          });
          recordRuntimeDiagnostic("chat.initial-parent-write-started", {
            sessionOrigin: SESSION_ORIGINS.NEW,
            persistenceState: "initial_parent_write_in_progress",
            selectedSessionId: String(normalized.id || ""),
            selectedSessionIdType: typeof normalized.id,
            documentPath: `orgs/${orgId}/sessions/${String(normalized.id || "")}`,
            writeOperation: "setDoc_merge_owner_parent",
            initialWriteOperationId,
          });
        }

        const parentWritten = await writeSessionToFirestore(
          generalSessionRecord,
          user,
          orgId
        );
        if (!parentWritten && initialWriteOperationId) {
          transitionSessionListener({
            type: "initial_write_failed",
            orgId,
            sessionId: normalized.id,
            operationId: initialWriteOperationId,
          });
          recordRuntimeDiagnostic("chat.initial-parent-write-result", {
            selectedSessionId: String(normalized.id || ""),
            documentPath: `orgs/${orgId}/sessions/${String(normalized.id || "")}`,
            initialWriteOperationId,
            parentWritten: false,
            serverReadable: false,
            gateOpened: false,
          });
        }
        if (!parentWritten) return false;
        const serverReadable = initialWriteOperationId
          ? await confirmOwnedSessionReadableFromServer(
              normalized.id,
              user,
              orgId
            )
          : true;
        if (!serverReadable) {
          if (initialWriteOperationId) {
            transitionSessionListener({
              type: "initial_write_failed",
              orgId,
              sessionId: normalized.id,
              operationId: initialWriteOperationId,
            });
          }
          recordRuntimeDiagnostic("chat.initial-parent-write-result", {
            selectedSessionId: String(normalized.id || ""),
            documentPath: `orgs/${orgId}/sessions/${String(normalized.id || "")}`,
            initialWriteOperationId,
            parentWritten,
            serverReadable: Boolean(serverReadable),
            gateOpened: false,
          });
          return false;
        }
        if (Object.keys(auditPatch).length) {
          await writeSessionFieldsToFirestore(
            normalized.id,
            auditPatch,
            user,
            orgId
          );
        }
        if (initialWriteOperationId) {
          transitionSessionListener({
            type: "initial_write_confirmed",
            orgId,
            sessionId: normalized.id,
            operationId: initialWriteOperationId,
          });
          recordRuntimeDiagnostic("chat.initial-parent-write-result", {
            selectedSessionId: String(normalized.id || ""),
            documentPath: `orgs/${orgId}/sessions/${String(normalized.id || "")}`,
            initialWriteOperationId,
            parentWritten: true,
            serverReadable: true,
            gateOpened: true,
          });
        }
        return true;
      })();
      if (initialWriteOperationId) {
        initialParentWritePromiseRef.current = {
          scopeKey: writeScopeKey,
          operationId: initialWriteOperationId,
          promise: remoteWrite,
        };
      }
      Object.defineProperty(normalized, SESSION_REMOTE_WRITE, {
        configurable: false,
        enumerable: false,
        value: remoteWrite,
        writable: false,
      });
    }
    return normalized;
  }, [canReviewOrgSessions, username, orgId, user, findById, transitionSessionListener]);

  const buildPersistedFollowUpRecommendation = useCallback((recommendation, summaryBase, operationalFollowUp, followUpId, sourceMessages = []) => {
    const recommendationSessionId = sessionKey(followUpId || summaryBase?.sessionId || operationalFollowUp?.sessionId || recommendation?.sessionId);
    const scopedSummaryBase = withSessionScope(summaryBase, recommendationSessionId);
    const normalizedFollowUp = withSessionScope(supersedeStaleSuggestedFollowUp(
      buildSessionFollowUp(operationalFollowUp, scopedSummaryBase),
      {
        sessionSummary: scopedSummaryBase,
        messages: sourceMessages,
      }
    ), recommendationSessionId);
    const normalizedRecommendation = buildSessionFollowUpRecommendation(
      recommendation,
      scopedSummaryBase,
      normalizedFollowUp,
      { messages: sourceMessages }
    );
    if (!normalizedRecommendation) return null;
    if (!(normalizedFollowUp?.scheduledFor && normalizedFollowUp?.status === "scheduled")) {
      return withSessionScope(normalizedRecommendation, recommendationSessionId);
    }
    return {
      ...normalizedRecommendation,
      sessionId: recommendationSessionId || normalizedRecommendation.sessionId || "",
      active: false,
      suggested: false,
      convertedToFollowUpAt:
        normalizedRecommendation.convertedToFollowUpAt ||
        normalizedFollowUp.updatedAt ||
        new Date().toISOString(),
      convertedFollowUpId:
        normalizedRecommendation.convertedFollowUpId ||
        String(followUpId || ""),
    };
  }, []);

  // ----- Initial greeting OR load a session by id -----
  useEffect(() => {
    const idParam = searchParams.get("id");
    const sidParam = searchParams.get("sid");

    if (idParam) {
      const pendingOrigin = pendingSessionOriginRef.current;
      const sessionOrigin =
        String(pendingOrigin.sessionId || "") === String(idParam)
          ? pendingOrigin.origin
          : SESSION_ORIGINS.URL;
      pendingSessionOriginRef.current = { sessionId: "", origin: "" };
      transitionSessionListener({
        type: "select_existing",
        origin: sessionOrigin,
        orgId,
        sessionId: idParam,
      });
      const found = findById(idParam);
      if (found?.rec) {
        setSessionId(String(found.rec.id));
        sessionStartRef.current = found.rec.createdAt || Date.now();
        const viewUser = found.rec.client || found.rec.user || username;

        if (found.rec.messages?.length) {
          setMessages(
            mapSessionMessagesForDisplay(found.rec.messages, {
              sessionId: found.rec.id,
              participantLabel: viewUser,
            })
          );
        }
        setNotes(found.rec.notes || { observations: "", reframes: "", next: "" });
        setAttachments(found.rec.attachments || []);
        setAssignments(found.rec.plan?.assignments || []);
        const activeSummary = resolveRecordSummaryForView(
          found.rec,
          recordMessagesForSummary(found.rec)
        );
        setSessionSummary(activeSummary);
        setSummaryDelivery(normalizeSummaryDelivery(found.rec.summaryDelivery));
        setPostSessionContinuation(found.rec.postSessionContinuation || null);
        setSessionCompletion(completionStateFromRecord(found.rec));
        setCompletionPersistence(completionPersistenceFromRecord(found.rec));
        setFollowUp(withSessionScope(buildSessionFollowUp(found.rec.followUp, activeSummary), found.rec.id));
        setFollowUpRecommendation(withSessionScope(buildSessionFollowUpRecommendation(found.rec.followUpRecommendation, activeSummary, found.rec.followUp), found.rec.id));
        setContinuationOffered(Boolean(found.rec.continuationOffered));
        setSessionPrivacy(normalizeSessionPrivacy(found.rec.privacy));
        setSessionSupportConsent(normalizeSupportConsent(found.rec.supportConsent));
        setSessionIdentity(
          role === "member"
            ? { ...memberIdentity, userEmail: user?.email || found.rec.userEmail || "" }
            : {
                client: found.rec.client || found.rec.user || username,
                user: found.rec.user || found.rec.client || username,
                userEmail: found.rec.userEmail || "",
                userId: found.rec.userId || "",
              }
        );
      }
      if (!found?.rec && user?.uid) {
        fetchOrgSessionById(user, orgId, idParam)
          .then((remote) => {
            if (!remote) return;
            const rec = normalizeRecord(remote, {
              name: username,
              userId: role === "member" ? user?.uid || "" : "",
              userEmail: user?.email || "",
            });
            setSessionId(String(rec.id));
            sessionStartRef.current = rec.createdAt || Date.now();
            const viewUser = rec.client || rec.user || username;
            if (rec.messages?.length) {
              setMessages(
                mapSessionMessagesForDisplay(rec.messages, {
                  sessionId: rec.id,
                  participantLabel: viewUser,
                })
              );
            }
            setNotes(rec.notes || { observations: "", reframes: "", next: "" });
            setAttachments(rec.attachments || []);
            setAssignments(rec.plan?.assignments || []);
            const activeSummary = resolveRecordSummaryForView(
              rec,
              recordMessagesForSummary(rec)
            );
            setSessionSummary(activeSummary);
            setSummaryDelivery(normalizeSummaryDelivery(rec.summaryDelivery));
            setPostSessionContinuation(rec.postSessionContinuation || null);
            setSessionCompletion(completionStateFromRecord(rec));
            setCompletionPersistence(completionPersistenceFromRecord(rec, true));
            setFollowUp(withSessionScope(buildSessionFollowUp(rec.followUp, activeSummary), rec.id));
            setFollowUpRecommendation(withSessionScope(buildSessionFollowUpRecommendation(rec.followUpRecommendation, activeSummary, rec.followUp), rec.id));
            setContinuationOffered(Boolean(rec.continuationOffered));
            setSessionPrivacy(normalizeSessionPrivacy(rec.privacy));
            setSessionSupportConsent(normalizeSupportConsent(rec.supportConsent));
            setSessionIdentity(
              role === "member"
                ? { ...memberIdentity, userEmail: user?.email || rec.userEmail || "" }
                : {
                    client: rec.client || rec.user || username,
                    user: rec.user || rec.client || username,
                    userEmail: rec.userEmail || "",
                    userId: rec.userId || "",
                  }
            );
          })
          .catch(() => {});
      }
      return;
    }

    // (Legacy sid support – index into per-user array)
    if (sidParam !== null) {
      const sid = Number(sidParam);
      if (Number.isFinite(sid)) {
        const perUser = getUserSessions();
        const rec = perUser[sid];
        if (rec) {
          transitionSessionListener({
            type: "select_existing",
            origin: SESSION_ORIGINS.HISTORY,
            orgId,
            sessionId: rec.id,
          });
          setSessionId(String(rec.id));
          sessionStartRef.current = rec.createdAt || Date.now();
        }
        if (rec?.messages?.length) {
          setMessages(
            mapSessionMessagesForDisplay(rec.messages, {
              sessionId: rec.id,
              participantLabel: rec.client || username,
            })
          );
        }
        setNotes(rec?.notes || { observations: "", reframes: "", next: "" });
        setAttachments(rec?.attachments || []);
        setAssignments(rec?.plan?.assignments || []);
        const activeSummary = resolveRecordSummaryForView(
          rec,
          recordMessagesForSummary(rec)
        );
        setSessionSummary(activeSummary);
        setSummaryDelivery(normalizeSummaryDelivery(rec?.summaryDelivery));
        setPostSessionContinuation(rec?.postSessionContinuation || null);
        setSessionCompletion(completionStateFromRecord(rec));
        setCompletionPersistence(completionPersistenceFromRecord(rec));
        setFollowUp(withSessionScope(buildSessionFollowUp(rec?.followUp, activeSummary), rec?.id));
        setFollowUpRecommendation(withSessionScope(buildSessionFollowUpRecommendation(rec?.followUpRecommendation, activeSummary, rec?.followUp), rec?.id));
        setContinuationOffered(Boolean(rec?.continuationOffered));
        setSessionPrivacy(normalizeSessionPrivacy(rec?.privacy));
        setSessionSupportConsent(normalizeSupportConsent(rec?.supportConsent));
        setSessionIdentity(
          role === "member"
            ? { ...memberIdentity, userEmail: user?.email || rec?.userEmail || "" }
            : {
                client: rec?.client || rec?.user || username,
                user: rec?.user || rec?.client || username,
                userEmail: rec?.userEmail || "",
                userId: rec?.userId || "",
              }
        );
      }
      return;
    }

    // Default greeting (no existing session yet)
    showDefaultGreeting();
  }, [searchParams, user, getUserSessions, findById, username, orgId, showDefaultGreeting, activeOrgId, role, memberIdentity, recordMessagesForSummary, transitionSessionListener]);

  useEffect(() => {
    if (searchParams.get("id") || searchParams.get("sid")) return;
    transitionSessionListener({ type: "clear" });
    setSessionId(null);
    sessionStartRef.current = null;
    setNotes({ observations: "", reframes: "", next: "" });
    setAttachments([]);
    setAssignments([]);
    setSessionSummary(null);
    setSummaryDelivery(normalizeSummaryDelivery(null));
    setPostSessionContinuation(null);
    setSessionCompletion(completionStateFromRecord());
    setCompletionPersistence(completionPersistenceFromRecord());
    setFollowUp(null);
    setFollowUpRecommendation(null);
    setContinuationOffered(false);
    setSessionPrivacy(normalizeSessionPrivacy(null));
    setSessionSupportConsent(normalizeSupportConsent(null));
    setSessionIdentity(memberIdentity);
    setCoachThread([]);
    setCoachInput("");
    setDismissedCoachMessageAt(0);
    coachInterruptHydratedRef.current = false;
    latestCoachTimestampRef.current = 0;
    showDefaultGreeting();
  }, [memberIdentity, orgId, searchParams, showDefaultGreeting, transitionSessionListener]);

  useEffect(() => {
    setCoachThread([]);
    if (!sessionId || !user?.uid || !selectedSessionDocumentReady) {
      recordRuntimeDiagnostic("coach-thread.listener-skipped", {
        selectedSessionId: sessionId,
        selectedSessionIdType: typeof sessionId,
        sessionOrigin: sessionListenerLifecycleRef.current.origin,
        persistenceState: sessionListenerLifecycleRef.current.state,
      });
      return undefined;
    }
    const threadOrgId = activeOrgId || user?.orgId || "org_default";
    const unsubscribe = listenCoachThread(user, threadOrgId, sessionId, (rows) => {
      const activeThreadSessionId = String(sessionId);
      setCoachThread(
        (Array.isArray(rows) ? rows : [])
          .map((row) => ({ ...row, sessionId: activeThreadSessionId }))
      );
    }, (error) => {
      recordRuntimeDiagnostic("chat.coach-thread-listener-error", {
        documentPath: `orgs/${threadOrgId}/sessions/${String(sessionId)}/coachThread`,
        code: String(error?.code || ""),
      });
    });
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [
    sessionId,
    user,
    activeOrgId,
    selectedSessionDocumentReady,
    sessionListenerLifecycle.listenerAttempt,
  ]);

  useEffect(() => {
    if (!selectedLiveSessionRecord) return;
    const rec = selectedLiveSessionRecord;
    recordRuntimeDiagnostic("chat.snapshot-projection-start", {
      renderCount: renderCountRef.current,
      selectedSessionId: sessionId,
      selectedSessionIdType: typeof sessionId,
      record: describeRuntimeSessionRecord(rec),
    });
    sessionStartRef.current = rec.createdAt || sessionStartRef.current;
    setSessionPrivacy(normalizeSessionPrivacy(rec.privacy));
    setSessionSupportConsent(normalizeSupportConsent(rec.supportConsent));
    const activeSummary = resolveRecordSummaryForView(
      rec,
      recordMessagesForSummary(rec)
    );
    setSessionSummary(activeSummary);
    setSummaryDelivery(normalizeSummaryDelivery(rec.summaryDelivery));
    setPostSessionContinuation(rec.postSessionContinuation || null);
    setSessionCompletion(completionStateFromRecord(rec));
    setCompletionPersistence(completionPersistenceFromRecord(rec, true));
    if (hasTrustedCompletedRecord(rec)) {
      const hydratedMessages = mapSessionMessagesForDisplay(rec.messages, {
        sessionId: rec.id,
        participantLabel: rec.client || rec.user || username,
      });
      latestMessagesRef.current = hydratedMessages;
      setMessages(hydratedMessages);
      setAnimatedAssistantMessageId(null);
    }
    setFollowUp(withSessionScope(buildSessionFollowUp(rec.followUp, activeSummary), rec.id));
    setFollowUpRecommendation(withSessionScope(buildSessionFollowUpRecommendation(rec.followUpRecommendation, activeSummary, rec.followUp), rec.id));
    setContinuationOffered(Boolean(rec.continuationOffered));
    setSessionIdentity((prev) => {
      if (role === "member") {
        return { ...memberIdentity, userEmail: user?.email || rec.userEmail || prev.userEmail || "" };
      }
      return {
        client: rec.client || rec.user || prev.client || username,
        user: rec.user || rec.client || prev.user || username,
        userEmail: rec.userEmail || prev.userEmail || "",
        userId: rec.userId || prev.userId || "",
      };
    });
    // The Firestore snapshot is the render authority. Updating the optional
    // local History cache must never gate or replace the React projection.
    try {
      persistSessionRecord(rec, { skipRemote: true });
    } catch {}
    recordRuntimeDiagnostic("chat.snapshot-projection-setters-called", {
      renderCount: renderCountRef.current,
      selectedSessionId: sessionId,
      record: describeRuntimeSessionRecord(rec),
      setters: [
        "setSessionPrivacy",
        "setSessionSupportConsent",
        "setSessionSummary",
        "setSummaryDelivery",
        "setPostSessionContinuation",
        "setSessionCompletion",
        "setCompletionPersistence",
        "setMessages",
        "setFollowUp",
        "setFollowUpRecommendation",
        "setContinuationOffered",
        "setSessionIdentity",
      ],
    });
  }, [
    memberIdentity,
    persistSessionRecord,
    recordMessagesForSummary,
    role,
    selectedLiveSessionRecord,
    sessionId,
    user?.email,
    username,
  ]);

  useEffect(() => {
    if (!sessionId || !user?.uid || isReadOnly) {
      setActiveInterventionCase(null);
      return undefined;
    }
    let mounted = true;
    const orgId = activeOrgId || user?.orgId || "org_default";
    const refresh = async () => {
      const row = await fetchCoachInterventionCaseBySession(user, orgId, sessionId);
      if (!mounted) return;
      setActiveInterventionCase(row);
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [sessionId, user, activeOrgId, isReadOnly]);

  const refreshActiveFlagCase = useCallback(async () => {
    if (!sessionId || !user?.uid || !canUseCoachTools) return;
    const orgId = activeOrgId || user?.orgId || "org_default";
    const row = await fetchCoachInterventionCaseBySession(user, orgId, sessionId);
    setActiveInterventionCase(row);
  }, [sessionId, user, activeOrgId, canUseCoachTools]);

  useEffect(() => {
    setResolutionType(
      resolutionSelectionForCase({
        status: activeInterventionCase?.status,
        resolutionType: activeInterventionCase?.resolutionType,
      })
    );
  }, [activeInterventionCase?.id, activeInterventionCase?.status, activeInterventionCase?.resolutionType]);

  const ensureActiveInterventionCase = useCallback(async () => {
    if (!sessionId || !user?.uid || !canUseCoachTools) return null;
    if (activeInterventionCase?.id) return activeInterventionCase;
    const orgId = activeOrgId || user?.orgId || "org_default";
    const scopedSummaryForCase = getSessionScopedValue(sessionSummary, sessionId);
    const shouldCreateFlagCase = hasActionableSessionFlagSuggestion({
      messages: toThreadMessages(messages),
      sessionSummary: scopedSummaryForCase,
    });
    const createCase = shouldCreateFlagCase ? createFlagCaseFromSession : createSessionInterventionCaseFromSession;
    const created = await createCase(user, orgId, {
      sessionId,
      sessionUser: sessionIdentity.user || sessionIdentity.client || username,
      sessionSummary: (sessionSummary?.summaryText || messages.find((m) => m.sender !== "NIA")?.text || "").slice(0, 160),
      source: shouldCreateFlagCase ? "nia" : undefined,
    });
    if (created) {
      setActiveInterventionCase(created);
      return created;
    }
    const refreshed = await fetchCoachInterventionCaseBySession(user, orgId, sessionId);
    if (refreshed) setActiveInterventionCase(refreshed);
    return refreshed;
  }, [activeInterventionCase, activeOrgId, canUseCoachTools, messages, sessionId, sessionIdentity.client, sessionIdentity.user, sessionSummary, toThreadMessages, user, username]);

  const handleClaimFlagCase = useCallback(async () => {
    if (!user?.uid) return;
    const orgId = activeOrgId || user?.orgId || "org_default";
    const caseRow = await ensureActiveInterventionCase();
    if (!caseRow?.id) {
      setFlagCaseStatus("Unable to open intervention case.");
      return;
    }
    setFlagCaseBusy(true);
    setFlagCaseStatus("");
    const result = await claimFlagCase(user, orgId, caseRow.id);
    if (!result?.ok) {
      setFlagCaseStatus(result?.reason === "claimed" ? "Already claimed by another coach." : "Unable to claim case.");
    } else {
      setFlagCaseStatus("Case claimed.");
    }
    await refreshActiveFlagCase();
    setFlagCaseBusy(false);
  }, [user, activeOrgId, refreshActiveFlagCase, ensureActiveInterventionCase]);

  const handleResolveFlagCase = useCallback(async (type) => {
    if (!user?.uid) return;
    const orgId = activeOrgId || user?.orgId || "org_default";
    const finalType = String(type || resolutionType || "intervened").toLowerCase();
    const caseRow = await ensureActiveInterventionCase();
    if (!caseRow?.id) {
      setFlagCaseStatus("Unable to open intervention case.");
      return;
    }
    setFlagCaseBusy(true);
    setFlagCaseStatus("");
    const result = await resolveFlagCase(user, orgId, caseRow.id, { resolutionType: finalType });
    if (!result?.ok) {
      setFlagCaseStatus(
        result?.reason === "claimed_by_other"
          ? "Claimed by another coach."
          : result?.reason === "resolved"
            ? "Case is already finalised."
            : "Unable to resolve case."
      );
    } else {
      setActiveInterventionCase((prev) => (
        prev
          ? {
              ...prev,
              status: result.status || prev.status,
              resolutionType: result.resolutionType || prev.resolutionType,
              updatedAt: Date.now(),
            }
          : prev
      ));
      setResolutionType(resolutionSelectionForCase({
        status: result.status,
        resolutionType: result.resolutionType,
      }));
      setFlagCaseStatus(`Case updated: ${getFlagCaseStatusLabel({ status: result.status })}.`);
    }
    await refreshActiveFlagCase();
    setFlagCaseBusy(false);
  }, [user, activeOrgId, refreshActiveFlagCase, resolutionType, ensureActiveInterventionCase]);

  const coachReadKey = useMemo(() => {
    if (!user?.uid || !sessionId) return null;
    return getCoachThreadReadKey(user.uid, sessionId) || null;
  }, [user, sessionId]);

  const activeCoachThread = useMemo(
    () => getSessionCoachThreadMessages(coachThread, sessionId),
    [coachThread, sessionId]
  );

  const coachUnread = useMemo(() => {
    if (!activeCoachThread.length || !coachReadKey) return 0;
    const lastCoach = activeCoachThread
      .filter((m) => m.senderRole === "coach" || m.senderRole === "admin" || m.senderRole === "superadmin")
      .reduce((max, m) => Math.max(max, Number(m.createdAt || 0)), 0);
    if (!lastCoach) return 0;
    const lastRead = getCoachThreadLastRead(user?.uid, sessionId);
    return lastCoach > lastRead ? 1 : 0;
  }, [activeCoachThread, coachReadKey, sessionId, user?.uid]);

  const latestCoachMessage = useMemo(() => {
    return activeCoachThread
      .filter((m) => m.senderRole === "coach" || m.senderRole === "admin" || m.senderRole === "superadmin")
      .reduce((latest, current) => {
        const currentTs = Number(current?.createdAt || 0);
        const latestTs = Number(latest?.createdAt || 0);
        return currentTs >= latestTs ? current : latest;
      }, null);
  }, [activeCoachThread]);
  const coachMessageCount = useMemo(
    () => activeCoachThread.filter((m) => m.senderRole === "coach" || m.senderRole === "admin" || m.senderRole === "superadmin").length,
    [activeCoachThread]
  );

  const latestCoachMessageAt = Number(latestCoachMessage?.createdAt || 0);
  const showCoachInterrupt = role === "member" && !!latestCoachMessage && coachUnread > 0 && latestCoachMessageAt > dismissedCoachMessageAt;

  useEffect(() => {
    if (!showCoachPanel || !coachReadKey) return;
    if (!activeCoachThread.length) return;
    markCoachThreadRead(user?.uid, sessionId, latestCoachMessageAt || Date.now());
    setDismissedCoachMessageAt((prev) => Math.max(prev, latestCoachMessageAt));
  }, [showCoachPanel, coachReadKey, activeCoachThread, latestCoachMessageAt, sessionId, user?.uid]);

  useEffect(() => {
    setDismissedCoachMessageAt(0);
    coachInterruptHydratedRef.current = false;
    latestCoachTimestampRef.current = 0;
  }, [sessionId]);

  useEffect(() => {
    if (role !== "member" || !sessionId) return;
    if (!latestCoachMessageAt) return;
    if (!coachInterruptHydratedRef.current) {
      coachInterruptHydratedRef.current = true;
      latestCoachTimestampRef.current = latestCoachMessageAt;
      return;
    }
    if (latestCoachMessageAt <= latestCoachTimestampRef.current) return;
    latestCoachTimestampRef.current = latestCoachMessageAt;
    setDismissedCoachMessageAt(0);
  }, [latestCoachMessageAt, role, sessionId]);

  // History list (per-user)
  const sessions = useMemo(() => {
    const cachedSessions = getUserSessions();
    if (!selectedLiveSessionRecord) return cachedSessions;
    return sortNewest([
      selectedLiveSessionRecord,
      ...cachedSessions.filter((record) => (
        !matchesSessionId(record?.id, selectedLiveSessionRecord.id)
      )),
    ]);
  }, [getUserSessions, matchesSessionId, selectedLiveSessionRecord]);
  const historyNeedsExpansion = sessions.length > HISTORY_DEFAULT_VISIBLE_COUNT;
  const visibleHistorySessions = useMemo(
    () => (
      historyExpanded || !historyNeedsExpansion
        ? sessions
        : sessions.slice(0, HISTORY_DEFAULT_VISIBLE_COUNT)
    ),
    [historyExpanded, historyNeedsExpansion, sessions]
  );
  const hiddenHistoryCount = Math.max(0, sessions.length - visibleHistorySessions.length);

  const ratingBase = sessionId || sessionStartRef.current;
  const ratingKey = useMemo(
    () => (ratingBase ? `nia_rating_${username}_${ratingBase}` : null),
    [ratingBase, username]
  );
  const ratingDismissKey = useMemo(
    () => (ratingBase ? `nia_rating_dismiss_${username}_${ratingBase}` : null),
    [ratingBase, username]
  );

  useEffect(() => {
    if (!sessionId && !sessionStartRef.current) return;
    if (!ratingKey) return;
    try {
      const stored = localStorage.getItem(ratingKey);
      if (stored) {
        setRatingSubmitted(true);
        setShowRating(false);
      }
      const dismissed = ratingDismissKey ? localStorage.getItem(ratingDismissKey) : null;
      if (dismissed === "1") {
        setRatingDismissed(true);
      }
    } catch {}
  }, [ratingKey, ratingDismissKey, sessionId]);

  useEffect(() => {
    if (!canUseChat || isReadOnly || ratingSubmitted || ratingDismissed) return;
    const totalMessages = messages.length;
    const userMessages = messages.filter((m) => m.sender !== "NIA").length;
    const thresholdReached = totalMessages >= 8 && userMessages >= 3;
    if (!thresholdReached) return;
    if (!ratingKey) return;
    try {
      const stored = localStorage.getItem(ratingKey);
      if (stored) return;
    } catch {}
    setShowRating(true);
  }, [messages, ratingSubmitted, ratingDismissed, isReadOnly, ratingKey, canUseChat]);

  // ----- Persist current thread into localStorage (UPDATE IN PLACE) -----
  const saveSession = useCallback((threadMessages, overrides = {}, options = {}) => {
    try {
      // Stable id & createdAt
      const createdAt = sessionStartRef.current || Date.now();
      const id = sessionId || createdAt;
      const mappedMessages = toThreadMessages(threadMessages);

      let record = createSessionFromThread(
        mappedMessages,
        { name: username }
      );

      // Overwrite identity/timestamps to be stable
      record.id = id;
      record.createdAt = createdAt;
      const activeSessionKey = sessionKey(id);
      const persisted = findById(id)?.rec || null;
      const scopedSessionSummary = getSessionScopedValue(sessionSummary, activeSessionKey);
      const scopedFollowUp = getSessionScopedValue(followUp, activeSessionKey);
      const scopedFollowUpRecommendation = getSessionScopedValue(followUpRecommendation, activeSessionKey);
      const persistedCompletionVersion =
        persisted?.completionVersion && typeof persisted.completionVersion === "object"
          ? persisted.completionVersion
          : null;
      const requestedCompletionVersion =
        overrides?.completionVersion && typeof overrides.completionVersion === "object"
          ? overrides.completionVersion
          : null;

      // Previews & metadata
      const firstUser = threadMessages.find((m) => m.sender !== "NIA")?.text || "";
      const firstNia = threadMessages.find((m) => m.sender === "NIA")?.text || "";
      record.user = overrides.user || sessionIdentity.user || memberIdentity.user;
      record.client = overrides.client || sessionIdentity.client || memberIdentity.client;
      record.userId = overrides.userId || sessionIdentity.userId || user?.uid || null;
      record.userEmail =
        overrides.userEmail ||
        sessionIdentity.userEmail ||
        user?.email ||
        record.userEmail ||
        null;
      record.date = createdAt;
      record.startedAt = overrides.startedAt || record.startedAt || createdAt;
      record.issueSummary = firstUser.slice(0, 140);
      record.advicePreview = firstNia.slice(0, 180);
      record.privacy = normalizeSessionPrivacy(overrides.privacy ?? sessionPrivacy ?? record.privacy);
      record.supportConsent = normalizeSupportConsent(overrides.supportConsent ?? sessionSupportConsent ?? record.supportConsent);
      record.notes = overrides.notes ?? notes;
      record.attachments = overrides.attachments ?? attachments;
      record.plan = { assignments: overrides.assignments ?? assignments };
      record.messageCount = mappedMessages.length;
      record.lastMessageAt =
        overrides.lastMessageAt ||
        (overrides.latestUserMessageText ? Date.now() : null) ||
        persisted?.lastMessageAt ||
        persisted?.createdAt ||
        createdAt;
      if (ratingData) record.rating = ratingData;
      record.completionVersion = persistedCompletionVersion || requestedCompletionVersion || null;
      record.sessionComplete = Boolean(
        persistedCompletionVersion
          ? persisted?.sessionComplete
          : persisted?.sessionComplete === true || overrides.sessionComplete === true
      );
      record.completionReason =
        persistedCompletionVersion
          ? String(persisted?.completionReason || "")
          : String(overrides.completionReason || persisted?.completionReason || "");
      record.completionAction =
        persistedCompletionVersion
          ? String(persisted?.completionAction || "")
          : String(overrides.completionAction || persisted?.completionAction || "");
      record.endedAt =
        persistedCompletionVersion
          ? persisted?.endedAt || persistedCompletionVersion.completedAt || null
          : overrides.endedAt ?? persisted?.endedAt ?? null;
      record.postSessionContinuation =
        persisted?.postSessionContinuation ||
        postSessionContinuation ||
        null;
      record.flagged = detectSessionFlag(record);
      const trustedFinalizationStatus = String(
        record.postSessionContinuation?.finalization?.status || ""
      ).toLowerCase();
      const hasTrustedFinalSummary = (
        ["succeeded", "reused"].includes(trustedFinalizationStatus) &&
        ["final", "reviewed"].includes(
          String(scopedSessionSummary?.generationStatus || scopedSessionSummary?.status || "").toLowerCase()
        ) &&
        scopedSessionSummary?.isCurrent !== false
      );
      const summaryInput = hasTrustedFinalSummary
        ? scopedSessionSummary
        : overrides.sessionSummary ?? scopedSessionSummary ?? persisted?.sessionSummary ?? record.sessionSummary ?? buildStructuredFallbackSummary(mappedMessages);
      const followUpInput = overrides.followUp ?? scopedFollowUp ?? persisted?.followUp ?? null;
      const provisionalSummary = normalizeStructuredSummary(summaryInput, mappedMessages, { followUp: followUpInput });
      record.followUp = withSessionScope(mergeSessionFollowUp(
        followUpInput,
        scopedFollowUp ?? persisted?.followUp,
        provisionalSummary
      ), activeSessionKey);
      record.followUp = completeOrdinaryFollowUpIfContinued({
        record,
        followUp: record.followUp,
        actorRole: role,
        memberContinued: Boolean(overrides.memberContinuedFollowUp),
        latestUserText: overrides.latestUserMessageText || "",
        coachActionRequired:
          typeof overrides.followUpCoachActionRequired === "boolean"
            ? overrides.followUpCoachActionRequired
            : null,
      });
      record.followUp = withSessionScope(record.followUp, activeSessionKey);
      record.sessionSummary = hasTrustedFinalSummary
        ? withSessionScope(summaryInput, activeSessionKey)
        : withSessionScope(stampSummarySourceVersion(
            preserveSummaryAuditFields(
              normalizeStructuredSummary(
                summaryInput,
                mappedMessages,
                { followUp: record.followUp }
              ),
              summaryInput
            ),
            mappedMessages,
            {
              preserveExisting: true,
              followUpFingerprint: sessionFollowUpFingerprint(record.followUp),
            }
          ), activeSessionKey);
      record.followUp = withSessionScope(supersedeStaleSuggestedFollowUp(
        record.followUp,
        {
          sessionSummary: {
            ...record.sessionSummary,
            sessionComplete: record.sessionComplete,
            completionReason: record.completionReason,
            completionAction: record.completionAction,
          },
          messages: mappedMessages,
        }
      ), activeSessionKey);
      const persistedCoachingState = buildCoachingState({
        messages: mappedMessages,
        sessionSummary: record.sessionSummary,
      });
      const canonicalOperationalRecord = applyCanonicalOperationalIntent(
        record,
        persistedCoachingState.operationalIntent
      );
      const boundaryCoachingState = {
        ...(canonicalOperationalRecord.sessionSummary?.coachingState || persistedCoachingState),
        sessionComplete: record.sessionComplete === true,
        completionReason: record.sessionComplete === true
          ? record.completionReason
          : "",
        completionAction: record.sessionComplete === true
          ? record.completionAction
          : "",
      };
      record.coachingState = boundaryCoachingState;
      record.sessionSummary = {
        ...canonicalOperationalRecord.sessionSummary,
        ...(record.sessionComplete === true
          ? {}
          : {
              generationStatus: "draft",
              status: "draft",
              canonical: false,
            }),
        coachingState: boundaryCoachingState,
      };
      record.currentStep = persistedCoachingState.currentStep;
      record.stepContext = {
        id: persistedCoachingState.currentStep?.id || "present",
        suggested: persistedCoachingState.currentStep?.id !== "present",
      };
      record.responseRoute = persistedCoachingState.responseRoute;
      record.detectedBlocks = Array.isArray(record.sessionSummary?.detectedBlocks)
        ? record.sessionSummary.detectedBlocks
        : [];
      record.limitingBeliefs = Array.isArray(persistedCoachingState.limitingBeliefs)
        ? persistedCoachingState.limitingBeliefs
        : [];
      record.recommendedGames = Array.isArray(persistedCoachingState.recommendedGames)
        ? persistedCoachingState.recommendedGames
        : [];
      record.flagSuggested = Boolean(record.sessionSummary?.flagSuggested);
      record.operationalIntent = canonicalOperationalRecord.operationalIntent;
      record.summaryDelivery = normalizeSummaryDelivery(
        overrides.summaryDelivery ?? summaryDelivery ?? persisted?.summaryDelivery
      );
      record.supportReview =
        overrides.supportReview ??
        persisted?.supportReview ??
        null;
      record.followUpRecommendation = withSessionScope(buildPersistedFollowUpRecommendation(
        overrides.followUpRecommendation ?? scopedFollowUpRecommendation,
        record.sessionSummary,
        record.followUp,
        id,
        mappedMessages
      ), activeSessionKey);
      record.continuationOffered = Boolean(overrides.continuationOffered ?? continuationOffered);
      record.followUpCreated = Boolean(record.followUp);
      record.sessionReengagementEligibleAt =
        overrides.sessionReengagementEligibleAt ||
        persisted?.sessionReengagementEligibleAt ||
        null;
      const contextualSafetyState = getSafetyContextState(mappedMessages);
      const latestMappedUserText = [...mappedMessages]
        .reverse()
        .find((entry) => entry.role === "user")?.content || "";
      const cumulativeSafetyAdjacent = Boolean(
        detectCumulativeSafetyAdjacentSignal(mappedMessages, latestMappedUserText)
      );
      const priorTriggerAt =
        persisted?.safetyState?.originalTriggerAt ||
        persisted?.flaggedAt ||
        record.flaggedAt ||
        null;
      const originalTriggerAt = priorTriggerAt || (record.flagged ? Date.now() : null);
      const explicitlyDeescalated = Boolean(
        contextualSafetyState.explicitDeescalation &&
        (originalTriggerAt || persisted?.flagged || persisted?.safetyState?.activePersonalSafety)
      );
      const currentActionableTriggerClassification = contextualSafetyState.activePersonalSafety
        ? "first_person_current"
        : record.flagged && cumulativeSafetyAdjacent
          ? "cumulative_safety_adjacent"
          : record.flagged
            ? "safety_review"
            : "none";
      const inferredTriggerClassification = currentActionableTriggerClassification !== "none"
        ? currentActionableTriggerClassification
        : contextualSafetyState.lastPersonalTriggerIndex >= 0
          ? "first_person_current"
          : contextualSafetyState.latestNonPersonalSafetyClassification !== "none"
            ? contextualSafetyState.latestNonPersonalSafetyClassification
            : cumulativeSafetyAdjacent
              ? "cumulative_safety_adjacent"
              : persisted?.flagged
                ? "legacy_safety_trigger"
                : "none";
      const originalTriggerClassification =
        persisted?.safetyState?.originalTriggerClassification ||
        (originalTriggerAt ? inferredTriggerClassification : "none");
      const latestTriggerClassification = record.flagged
        ? currentActionableTriggerClassification
        : persisted?.safetyState?.latestTriggerClassification || originalTriggerClassification;
      const resolvedTriggerClassification = explicitlyDeescalated
        ? persisted?.safetyState?.latestTriggerClassification || originalTriggerClassification
        : latestTriggerClassification;
      const falsePositiveTrigger = [
        "third_person_current",
        "third_person_historical",
        "quoted_or_informational",
        "ambiguous_historical",
      ].includes(resolvedTriggerClassification);
      if (originalTriggerAt) {
        record.flaggedAt = originalTriggerAt;
      }
      record.safetyState = {
        ...(persisted?.safetyState || {}),
        activePersonalSafety: Boolean(contextualSafetyState.activePersonalSafety),
        status: contextualSafetyState.activePersonalSafety
          ? "active_personal_safety"
          : record.flagged
            ? "safety_adjacent_review"
            : explicitlyDeescalated
              ? "deescalated"
              : contextualSafetyState.classification,
        classification: contextualSafetyState.classification,
        deescalationType: explicitlyDeescalated
          ? falsePositiveTrigger
            ? "false_positive_non_personal_context"
            : "personal_risk_clarified_safe"
          : persisted?.safetyState?.deescalationType || "",
        originalTriggerAt,
        originalTriggerClassification,
        latestTriggerClassification,
        deescalatedAt: explicitlyDeescalated
          ? persisted?.safetyState?.deescalatedAt || Date.now()
          : persisted?.safetyState?.deescalatedAt || null,
        updatedAt: Date.now(),
      };

      // Upsert in both per-user and global buckets
      const normalized = persistSessionRecord(record, {
        skipRemote: Boolean(options?.skipRemote),
        auditFields: ["summaryDelivery", "supportReview"].filter((field) => (
          Object.prototype.hasOwnProperty.call(overrides, field)
        )),
      });
      if (!options?.silent) {
        setSessionSummary(withSessionScope(normalized.sessionSummary, id));
        setSummaryDelivery(normalizeSummaryDelivery(normalized.summaryDelivery));
        setPostSessionContinuation(normalized.postSessionContinuation || null);
        setSessionCompletion(completionStateFromRecord(normalized));
        setFollowUp(withSessionScope(normalized.followUp, id));
        setFollowUpRecommendation(withSessionScope(normalized.followUpRecommendation, id));
        setContinuationOffered(Boolean(normalized.continuationOffered));
        setSessionPrivacy(normalizeSessionPrivacy(normalized.privacy));
        setSessionSupportConsent(normalizeSupportConsent(normalized.supportConsent));
        setSessionIdentity((prev) => ({
          client: normalized.client || normalized.user || prev.client || username,
          user: normalized.user || normalized.client || prev.user || username,
          userEmail: normalized.userEmail || user?.email || prev.userEmail || "",
          userId: normalized.userId || prev.userId || user?.uid || "",
        }));
      }
      if (record.flagged) {
        try {
          const flagKey = `nia_flagged_${id}`;
          const existing = localStorage.getItem(flagKey);
          const reviewIdentityLabel = getSessionDisplayName(record, "coach");
          if (existing !== "1") {
            localStorage.setItem(flagKey, "1");
            logOrgActivity(user, activeOrgId || user?.orgId || "org_default", "session_flagged", {
              sessionId: id,
              user: reviewIdentityLabel,
              source: "nia",
            });
            notifyCoachForOrg(user, activeOrgId || user?.orgId || "org_default", {
              subject: "Flagged session detected",
              message: `Flagged session ${id} by ${reviewIdentityLabel}: ${(record.issueSummary || "").slice(0, 120)}`,
            });
          }
          createFlagCaseFromSession(user, activeOrgId || user?.orgId || "org_default", {
            sessionId: id,
            sessionUser: reviewIdentityLabel,
            sessionSummary: (record.issueSummary || "").slice(0, 160),
            source: "nia",
          }).then((created) => {
            if (!created?.id) return;
            if (matchesSessionId(sessionId || id, id)) {
              setActiveInterventionCase(created);
            }
          }).catch((err) => {
            console.warn("Flag case creation call failed", {
              orgId: activeOrgId || user?.orgId || "org_default",
              sessionId: id,
              reason: err?.message || String(err || "unknown"),
            });
          });
        } catch {}
      }
      if (!options?.silent && !options?.skipSummaryRefresh) {
        const parentWrite = normalized?.[SESSION_REMOTE_WRITE];
        if (parentWrite) {
          const markParentWriteFailure = () => {
            const latestRecord = findById(id)?.rec || normalized;
            if (
              sessionMessageFingerprint(latestRecord?.messages || []) !==
              sessionMessageFingerprint(normalized?.messages || [])
            ) {
              return;
            }
            const failedRecord = persistSessionRecord({
              ...latestRecord,
              sessionSummary: buildSessionPersistenceFailureSummary(
                latestRecord?.sessionSummary,
                id
              ),
            }, { skipRemote: true });
            if (
              failedRecord &&
              mountedRef.current &&
              matchesSessionId(sessionId || id, id)
            ) {
              setSessionSummary(withSessionScope(failedRecord.sessionSummary, id));
            }
          };
          parentWrite.then((parentWritten) => {
            if (!parentWritten) markParentWriteFailure();
          }).catch(markParentWriteFailure);
        }
      }
      return normalized;
    } catch {
      // no-op
      return null;
    }
  }, [
    activeOrgId,
    assignments,
    attachments,
    followUpRecommendation,
    followUp,
    findById,
    notes,
    buildPersistedFollowUpRecommendation,
    continuationOffered,
    postSessionContinuation,
    persistSessionRecord,
    ratingData,
    sessionId,
    sessionSummary,
    summaryDelivery,
    sessionSupportConsent,
    sessionPrivacy,
    toThreadMessages,
    user,
    memberIdentity.client,
    memberIdentity.user,
    matchesSessionId,
    sessionIdentity.client,
    sessionIdentity.user,
    sessionIdentity.userEmail,
    sessionIdentity.userId,
    role,
    username,
  ]);
  const applyFollowUpPatch = useCallback((patch, actorType = "coach") => {
    const activeSessionKey = sessionKey(sessionId || sessionStartRef.current || "");
    const scopedSessionSummary = getSessionScopedValue(sessionSummary, activeSessionKey);
    const scopedFollowUp = getSessionScopedValue(followUp, activeSessionKey);
    const scopedFollowUpRecommendation = getSessionScopedValue(followUpRecommendation, activeSessionKey);
    const summaryBase = normalizeStructuredSummary(
      scopedSessionSummary || buildStructuredFallbackSummary(toThreadMessages(messages)),
      toThreadMessages(messages),
      { followUp: scopedFollowUp }
    );
    const scopedSummaryBase = withSessionScope(summaryBase, activeSessionKey);
    const current = buildSessionFollowUp(scopedFollowUp, scopedSummaryBase);
    const createdByType = current?.createdByType || actorType;
    const nextScheduledFor = patch?.scheduledFor || current?.scheduledFor || null;
    const finalStatusPatch = patch?.status === "completed"
      ? {
          completedAt: patch?.completedAt || new Date().toISOString(),
          dismissedAt: null,
        }
      : patch?.status === "dismissed"
        ? {
            dismissedAt: patch?.dismissedAt || new Date().toISOString(),
            completedAt: null,
          }
        : patch?.status === "scheduled"
          ? {
              completedAt: null,
              dismissedAt: null,
            }
          : {};
    const schedulingPatch = patch?.status === "scheduled"
      ? {
          coachConfirmed: actorType === "coach" ? true : Boolean(patch?.coachConfirmed || current?.coachConfirmed),
          reminderAt: patch?.reminderAt || getDefaultFollowUpReminderAt(nextScheduledFor),
          reminderSentAt: patch?.reminderSentAt || null,
        }
      : {};
    const next = buildSessionFollowUp(
      {
        ...(current || {}),
        ...(patch || {}),
        ...finalStatusPatch,
        ...schedulingPatch,
        createdByType,
        updatedByType: actorType,
        updatedAt: new Date().toISOString(),
      },
      summaryBase
    );
    const reconciledSummaryBase = withSessionScope(normalizeStructuredSummary(
      summaryBase,
      toThreadMessages(messages),
      { followUp: next }
    ), activeSessionKey);
    const shouldConvertRecommendation = Boolean(next?.scheduledFor || patch?.status === "scheduled");
    const nextRecommendation = shouldConvertRecommendation
      ? buildPersistedFollowUpRecommendation(
          {
            ...(followUpRecommendation || {}),
            convertedToFollowUpAt: new Date().toISOString(),
            convertedFollowUpId: String(sessionId || sessionStartRef.current || ""),
          },
          reconciledSummaryBase,
          next,
          sessionId || sessionStartRef.current || "",
          toThreadMessages(messages)
        )
      : buildPersistedFollowUpRecommendation(
          scopedFollowUpRecommendation,
          reconciledSummaryBase,
          next,
          sessionId || sessionStartRef.current || "",
          toThreadMessages(messages)
        );
    setSessionSummary(reconciledSummaryBase);
    setFollowUp(withSessionScope(next, activeSessionKey));
    setFollowUpRecommendation(withSessionScope(nextRecommendation, activeSessionKey));
    if (next && (sessionId || messages.length > 1)) {
      saveSession(messages, { followUp: next, followUpRecommendation: nextRecommendation, sessionSummary: reconciledSummaryBase });
    }
  }, [buildPersistedFollowUpRecommendation, followUp, followUpRecommendation, messages, saveSession, sessionId, sessionSummary, toThreadMessages]);

  const dismissFollowUpRecommendation = useCallback(() => {
    const activeSessionKey = sessionKey(sessionId || sessionStartRef.current || "");
    const scopedSessionSummary = getSessionScopedValue(sessionSummary, activeSessionKey);
    const scopedFollowUp = getSessionScopedValue(followUp, activeSessionKey);
    const scopedFollowUpRecommendation = getSessionScopedValue(followUpRecommendation, activeSessionKey);
    const summaryBase = normalizeStructuredSummary(
      scopedSessionSummary || buildStructuredFallbackSummary(toThreadMessages(messages)),
      toThreadMessages(messages),
      { followUp: scopedFollowUp }
    );
    const scopedSummaryBase = withSessionScope(summaryBase, activeSessionKey);
    const nextRecommendation = buildSessionFollowUpRecommendation(
      {
        ...(scopedFollowUpRecommendation || {}),
        dismissedAt: new Date().toISOString(),
      },
      scopedSummaryBase,
      scopedFollowUp
    );
    setFollowUpRecommendation(withSessionScope(nextRecommendation, activeSessionKey));
    if (sessionId || messages.length > 1) {
      saveSession(messages, { followUpRecommendation: nextRecommendation, sessionSummary: scopedSummaryBase });
    }
  }, [followUp, followUpRecommendation, messages, saveSession, sessionId, sessionSummary, toThreadMessages]);

  // ----- Send -----
  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (isReadOnly) return;
    if (loading) return;

    const trimmed = input.trim();
    if (!trimmed) return;

    let activeTurnSessionId = sessionId || sessionStartRef.current || "";

    // Create a stable session id at first user message if not present
    if (!sessionId) {
      const ts = Date.now();
      activeTurnSessionId = ts;
      sessionStartRef.current = ts;
      transitionSessionListener({
        type: "select_new",
        orgId,
        sessionId: ts,
      });
      setSessionId(ts);
      setSessionSummary(null);
      setSummaryDelivery(normalizeSummaryDelivery(null));
      setFollowUp(null);
      setFollowUpRecommendation(null);
      setCoachThread([]);
      logOrgActivity(user, activeOrgId || user?.orgId || "org_default", "session_created", {
        sessionId: ts,
      });
    }
    if (!sessionStartRef.current) {
      sessionStartRef.current = Date.now();
      activeTurnSessionId = activeTurnSessionId || sessionStartRef.current;
    }
    const activeTurnSessionKey = sessionKey(activeTurnSessionId);

    if (completedSessionLocked) {
      setReturnSignalError(
        "This completed transcript is locked while its final summary is processed. Start a new session to continue."
      );
      return;
    }

    // Points & counters
    incMessage();
    addPoints(1);
    try {
      const curMsg = Number(localStorage.getItem("nia_messages") || 0) + 1;
      localStorage.setItem("nia_messages", String(curMsg));
      const curPts = Number(localStorage.getItem("nia_points") || 0) + 1;
      localStorage.setItem("nia_points", String(curPts));
    } catch {}

    // Local append
    const you = username;
    const userMsg = { id: Date.now(), sender: you, text: trimmed, createdAt: new Date().toISOString() };
    const liveMessages = Array.isArray(latestMessagesRef.current) && latestMessagesRef.current.length
      ? latestMessagesRef.current
      : messages;
    const base = [...liveMessages, userMsg];
    const history = toThreadMessages(base);
    const existingTurnFollowUp = getSessionScopedValue(followUp, activeTurnSessionKey);
    const turnFallbackSummary = withSessionScope(
      buildStructuredFallbackSummary(history, { followUp: existingTurnFollowUp }),
      activeTurnSessionKey
    );
    const turnSessionSummary = resolveActiveSessionSummary({
      sessionSummary,
      fallbackSummary: turnFallbackSummary,
      sessionId: activeTurnSessionKey,
      messages: history,
      followUp: existingTurnFollowUp,
    });
    const turnFollowUp = withSessionScope(
      buildSessionFollowUp(existingTurnFollowUp, turnSessionSummary),
      activeTurnSessionKey
    );
    const turnFollowUpRecommendation = getSessionScopedValue(followUpRecommendation, activeTurnSessionKey);
    const turnFollowUpState = getFollowUpState(turnFollowUp);
    const followUpTurnContext = {
      ...sessionViewRecord,
      id: activeTurnSessionKey || sessionViewRecord.id,
      followUp: turnFollowUp,
      activeInterventionStatus: activeInterventionCase?.status || "",
      messages: history,
      issueSummary: turnSessionSummary?.summaryText || "",
      advicePreview: turnSessionSummary?.suggestedNextStep || "",
      sessionSummary: turnSessionSummary,
      supportConsent: sessionSupportConsent,
    };
    const followUpCoachActionRequired = isCoachActionRequiredFollowUp(followUpTurnContext, turnFollowUp);
    const resumedFollowUpAt = new Date().toISOString();
    const memberContinuedFollowUp = Boolean(
      role === "member" &&
      resumeFollowUp &&
      turnFollowUp?.status === "scheduled" &&
      !followUpCoachActionRequired
    );
    const continuedFollowUp = memberContinuedFollowUp
      ? {
          ...(turnFollowUp || {}),
          lastOpenedAt: turnFollowUp?.lastOpenedAt || resumedFollowUpAt,
          resumedAt: turnFollowUp?.resumedAt || resumedFollowUpAt,
          updatedAt: resumedFollowUpAt,
          updatedByType: "nia",
        }
      : turnFollowUp;
    setMessages(base);
    latestMessagesRef.current = base;
    setInput("");
    setLoading(true);

    const activeCaseStatus = String(activeInterventionCase?.status || "").trim();
    const activeReviewOpen = Boolean(
      activeInterventionCase?.id && !isFinalFlagStatus(activeCaseStatus)
    );
    const turnCoachingState = buildCoachingState({
      messages: history,
      sessionSummary: turnSessionSummary,
    });
    let continuityPacket = null;
    if (
      role === "member" &&
      user?.uid &&
      shouldRetrieveSameUserContinuity(trimmed)
    ) {
      try {
        const sameUserSessions = await fetchSameUserOrgSessions(user, orgId, user.uid);
        const boundedPacket = buildSameUserContinuityPacket({
          sessions: sameUserSessions,
          orgId,
          userId: user.uid,
          currentSessionId: activeTurnSessionKey,
          requestText: trimmed,
        });
        continuityPacket = {
          ...boundedPacket,
          orgId,
          userId: user.uid,
        };
      } catch {
        continuityPacket = {
          available: false,
          reason: "lookup_failed_closed",
          sessions: [],
          orgId,
          userId: user.uid,
        };
      }
    }
    const chatRequestBody = {
      messages: history,
      coachingState: turnCoachingState,
      followUp: continuedFollowUp,
      followUpState: turnFollowUpState,
      sessionSummary: turnSessionSummary,
      userEmail: sessionIdentity.userEmail || user?.email || "",
      continuationOffered,
      resumeFollowUp: resumeFollowUp && continuedFollowUp?.status === "scheduled",
      chatSurface: "main_chat_page",
      signedInRole: role,
      userLocation: buildUserLocationContext(user),
      sessionFlagged: Boolean(turnSessionSummary?.flagSuggested),
      coachReviewOpen: activeReviewOpen,
      activeInterventionCaseId: activeReviewOpen ? activeInterventionCase?.id || "" : "",
      activeInterventionStatus: activeInterventionCase?.status || "",
      flagCaseStatus: sessionViewRecord?.flagCaseStatus || activeInterventionCase?.status || "",
    };
    const fallbackRequestBody = {
      text: trimmed,
      coachingState: buildCoachingState({
        latestUserMessage: trimmed,
        recentAssistantMessages: liveMessages.filter((m) => m.sender === "NIA").slice(-2).map((m) => m.text),
        sessionSummary: turnSessionSummary,
      }),
      chatSurface: "main_chat_page",
      signedInRole: role,
      userEmail: sessionIdentity.userEmail || user?.email || "",
      userLocation: buildUserLocationContext(user),
      sessionFlagged: Boolean(turnSessionSummary?.flagSuggested),
      coachReviewOpen: activeReviewOpen,
      activeInterventionCaseId: activeReviewOpen ? activeInterventionCase?.id || "" : "",
      activeInterventionStatus: activeInterventionCase?.status || "",
      flagCaseStatus: sessionViewRecord?.flagCaseStatus || activeInterventionCase?.status || "",
    };
    const requestChatPayload = async (body, attempt = 0, clientRetryOf = "") => {
      const resp = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...body,
          clientRetryOf: clientRetryOf || undefined,
          clientRetryAttempt: attempt,
        }),
      });
      if (resp.status === 400 && body.messages) {
        return requestChatPayload(fallbackRequestBody, attempt, clientRetryOf);
      }
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      return resp.json();
    };
    const operationalSupportRequested = Boolean(turnCoachingState?.operationalIntent?.operational);
    const existingSupportReview = findById(activeTurnSessionKey)?.rec?.supportReview || null;
    const buildSupportReviewState = (meta = {}) => {
      if (!operationalSupportRequested && !meta?.supportReviewRequested) return null;
      const requestedAt =
        existingSupportReview?.requestedAt ||
        new Date().toISOString();
      return {
        ...(existingSupportReview || {}),
        status: "requested",
        sourceSessionId: String(activeTurnSessionKey || ""),
        requestedAt,
        requestingActorType: role || "member",
        reason:
          meta?.supportReviewReason ||
          turnCoachingState?.operationalIntent?.reason ||
          "operational_or_continuity_issue",
        continuityContextAvailable: Boolean(continuityPacket?.available),
        updatedAt: new Date().toISOString(),
      };
    };
    const openSupportReviewCase = (supportReviewState) => {
      if (!supportReviewState || !activeTurnSessionKey || !user?.uid) return;
      createSessionInterventionCaseFromSession(user, orgId, {
        sessionId: activeTurnSessionKey,
        sessionUser: sessionIdentity.user || sessionIdentity.client || username,
        sessionSummary: (
          turnSessionSummary?.summaryText ||
          trimmed
        ).slice(0, 160),
        reviewKind: "operational_support",
        reviewReason: supportReviewState.reason,
        source: "nia_operational_support",
      }).then((created) => {
        if (created?.id && matchesSessionId(activeTurnSessionKey, sessionId || activeTurnSessionKey)) {
          setActiveInterventionCase(created);
        }
      }).catch(() => {});
    };
    const operationalSafety = turnCoachingState?.safetyEscalation || {};
    const canUseLocalOperationalSupport = Boolean(
      operationalSupportRequested &&
      !operationalSafety.safetySignal &&
      !operationalSafety.ambiguousSafetySignal &&
      !operationalSafety.cumulativeSafetyAdjacentSignal &&
      !operationalSafety.realHelpSignal
    );
    const supportReviewReason = turnCoachingState?.operationalIntent?.inaccurateRecap
      ? "inaccurate_recap"
      : turnCoachingState?.operationalIntent?.missingSummary ||
          turnCoachingState?.operationalIntent?.deliveryComplaint
        ? "missing_summary_or_delivery"
        : turnCoachingState?.operationalIntent?.reminderComplaint
          ? "reminder_accuracy"
          : turnCoachingState?.operationalIntent?.escalationRequest
            ? "operational_escalation"
            : "records_or_continuity_issue";
    const localOperationalPayload = canUseLocalOperationalSupport
      ? {
          text: buildOperationalSupportResponse(
            turnCoachingState.operationalIntent,
            {
              continuityPacket,
              identity: {
                orgId,
                userId: user?.uid || "",
              },
            }
          ),
          followUp: null,
          continuationOffered: false,
          sessionCompleted: false,
          meta: {
            responseRoute: "support",
            operationalIntentTriggered: true,
            operationalReviewRecommended: true,
            supportReviewRequested: true,
            supportReviewReason,
            reviewFlagRecommended: false,
            continuityPacketUsed: Boolean(continuityPacket?.available),
            continuityPacketRejected: false,
            degraded: false,
          },
        }
      : null;

    // Server call
    try {
      let payload;
      if (localOperationalPayload) {
        payload = localOperationalPayload;
      } else {
        try {
          payload = await requestChatPayload(chatRequestBody);
        } catch (firstError) {
          console.warn("chat request retry triggered:", {
            failureStage: "frontend_fetch_failure",
            retryTriggered: true,
            timeoutDetected: false,
            visibleFailureShownToUser: false,
            detail: String(firstError?.message || firstError || "").slice(0, 120),
          });
          payload = await requestChatPayload(chatRequestBody, 1);
          console.info("chat request retry succeeded:", {
            failureStage: "frontend_fetch_failure",
            retryTriggered: true,
            retrySucceeded: true,
            visibleFailureShownToUser: false,
          });
        }
      }
      if (payload?.meta?.clearActiveSafetyState) {
        setActiveInterventionCase((current) => {
          if (!current?.id || isFinalFlagStatus(current?.status)) return current;
          if (String(current?.caseType || "flagged").toLowerCase() !== "flagged") return current;
          if (String(current?.source || "nia").toLowerCase() !== "nia") return current;
          return {
            ...current,
            status: "resolved_no_action",
            resolutionType: payload?.meta?.deescalationType === "personal_risk_clarified_safe"
              ? "reviewed_no_action"
              : "false_positive",
            autoResolutionPending: true,
            updatedAt: Date.now(),
          };
        });
      }
      const assistantText = payload?.text;
      const niaFollowUp = mergeSessionFollowUp(payload?.followUp, continuedFollowUp || turnFollowUp, turnSessionSummary);
      const nextContinuationOffered = Boolean(payload?.continuationOffered);
      const supportReviewState = buildSupportReviewState(payload?.meta || {});
      const completionRequested = payload?.sessionCompleted === true;
      const successReply = {
        id: Date.now() + 2,
        sender: "NIA",
        text: stripMarkdownFormatting(
          assistantText || "I'm here with you. Let's take this one step at a time."
        ),
        createdAt: new Date().toISOString(),
      };
      const successfulThread = [...base, successReply];
      const completionAt = payload?.sessionCompleted
        ? payload?.endedAt || new Date().toISOString()
        : "";
      const completionVersion = (
        completionRequested &&
        canPersistOwnedCompletion &&
        !sessionCompletion.completionVersion?.id
      )
        ? buildSessionCompletionVersion({
            orgId,
            userId: sessionIdentity.userId || user?.uid || "",
            sessionId: activeTurnSessionKey,
            messages: toThreadMessages(successfulThread),
            completedAt: completionAt,
          })
        : null;

      const saveOverrides = {
        followUp: niaFollowUp || turnFollowUp,
        followUpRecommendation: turnFollowUpRecommendation,
        sessionSummary: turnSessionSummary,
        continuationOffered: nextContinuationOffered,
        memberContinuedFollowUp,
        followUpCoachActionRequired,
        latestUserMessageText: trimmed,
        supportReview: supportReviewState,
      };

      if (completionRequested) {
        setCompletionPersistence({
          status: "pending",
          versionId: completionVersion?.id || "",
          lastError: "",
        });
        const provisionalRecord = completionVersion
          ? saveSession(successfulThread, {
              ...saveOverrides,
              sessionComplete: true,
              completionReason:
                payload?.completionReason || turnCoachingState?.completionReason || "",
              completionAction:
                payload?.completionAction || turnCoachingState?.completionAction || "",
              completionVersion,
              endedAt: completionAt,
            }, {
              silent: true,
              skipRemote: true,
              skipSummaryRefresh: true,
            })
          : null;
        recordRuntimeDiagnostic("chat.local-completion-provisional", {
          renderCount: renderCountRef.current,
          selectedSessionId: activeTurnSessionKey,
          completionVersionId: String(completionVersion?.id || ""),
          record: describeRuntimeSessionRecord(provisionalRecord),
        });
        const completionScopeKey = `${orgId}:${String(activeTurnSessionKey)}`;
        const pendingInitialWrite = initialParentWritePromiseRef.current;
        if (
          sessionListenerLifecycleRef.current.scopeKey === completionScopeKey &&
          sessionListenerLifecycleRef.current.state ===
            "initial_parent_write_in_progress" &&
          pendingInitialWrite.scopeKey === completionScopeKey &&
          pendingInitialWrite.promise
        ) {
          await pendingInitialWrite.promise.catch(() => false);
        }
        let completionInitialWriteOperationId = "";
        if (shouldStartInitialParentWrite(
          sessionListenerLifecycleRef.current,
          { orgId, sessionId: activeTurnSessionKey }
        )) {
          initialParentWriteCounterRef.current += 1;
          completionInitialWriteOperationId = [
            "initial-parent-completion",
            completionScopeKey,
            initialParentWriteCounterRef.current,
          ].join(":");
          transitionSessionListener({
            type: "initial_write_started",
            orgId,
            sessionId: activeTurnSessionKey,
            operationId: completionInitialWriteOperationId,
            writeKind: "completion_transaction_parent_set",
          });
          recordRuntimeDiagnostic("chat.initial-parent-write-started", {
            sessionOrigin: SESSION_ORIGINS.NEW,
            persistenceState: "initial_parent_write_in_progress",
            selectedSessionId: String(activeTurnSessionKey),
            selectedSessionIdType: typeof activeTurnSessionId,
            documentPath: `orgs/${orgId}/sessions/${String(activeTurnSessionKey)}`,
            writeOperation: "completion_transaction_owner_parent",
            initialWriteOperationId: completionInitialWriteOperationId,
          });
        }
        let completionPersisted = Boolean(
          provisionalRecord &&
          await writeSessionCompletionToFirestore(
            provisionalRecord,
            user,
            orgId
          )
        );
        if (completionInitialWriteOperationId) {
          const serverReadable = completionPersisted &&
            await confirmOwnedSessionReadableFromServer(
              activeTurnSessionKey,
              user,
              orgId
            );
          completionPersisted = Boolean(completionPersisted && serverReadable);
          transitionSessionListener({
            type: completionPersisted
              ? "initial_write_confirmed"
              : "initial_write_failed",
            orgId,
            sessionId: activeTurnSessionKey,
            operationId: completionInitialWriteOperationId,
          });
          recordRuntimeDiagnostic("chat.initial-parent-write-result", {
            selectedSessionId: String(activeTurnSessionKey),
            documentPath: `orgs/${orgId}/sessions/${String(activeTurnSessionKey)}`,
            initialWriteOperationId: completionInitialWriteOperationId,
            parentWritten: completionPersisted,
            serverReadable: Boolean(serverReadable),
            gateOpened: completionPersisted,
          });
        }
        recordRuntimeDiagnostic("chat.local-completion-persisted", {
          renderCount: renderCountRef.current,
          selectedSessionId: activeTurnSessionKey,
          completionVersionId: String(completionVersion?.id || ""),
          completionPersisted,
          liveRecordAtResolution: describeRuntimeSessionRecord(
            liveSessionRecordRef.current
          ),
        });
        if (completionPersisted) {
          await runRuntimeTestHook("afterCompletionPersisted", {
            selectedSessionId: String(activeTurnSessionKey || ""),
            completionVersionId: String(completionVersion?.id || ""),
          });
        }

        if (completionPersisted) {
          const persistedSuccessThread = [
            ...base,
            {
              ...successReply,
              text: resolveCompletionAssistantText({
                sessionCompleted: true,
                persistenceSucceeded: true,
                successText: successReply.text,
                correlationId: payload?.meta?.correlationId || "",
              }),
            },
          ];
          const authoritativeCompletionRecord = liveSessionRecordRef.current;
          const authoritativeCompletionVisible = hasMatchingAuthoritativeCompletion(
            authoritativeCompletionRecord,
            completionVersion?.id
          );
          recordRuntimeDiagnostic("chat.local-completion-resolution", {
            renderCount: renderCountRef.current,
            selectedSessionId: activeTurnSessionKey,
            completionVersionId: String(completionVersion?.id || ""),
            authoritativeCompletionVisible,
            authoritativeRecord: describeRuntimeSessionRecord(
              authoritativeCompletionRecord
            ),
            provisionalRecord: describeRuntimeSessionRecord(provisionalRecord),
          });
          if (!authoritativeCompletionVisible) {
            saveSession(persistedSuccessThread, {
              ...saveOverrides,
              sessionComplete: true,
              completionReason:
                payload?.completionReason || turnCoachingState?.completionReason || "",
              completionAction:
                payload?.completionAction || turnCoachingState?.completionAction || "",
              completionVersion,
              endedAt: completionAt,
            }, {
              skipRemote: true,
              skipSummaryRefresh: true,
            });
          }
          const authoritativeMessages = authoritativeCompletionVisible
            ? mapSessionMessagesForDisplay(authoritativeCompletionRecord.messages, {
                sessionId: authoritativeCompletionRecord.id,
                participantLabel:
                  authoritativeCompletionRecord.client ||
                  authoritativeCompletionRecord.user ||
                  username,
              })
            : persistedSuccessThread;
          setMessages(authoritativeMessages);
          latestMessagesRef.current = authoritativeMessages;
          setAnimatedAssistantMessageId(successReply.id);
          setContinuationOffered(false);
        } else {
          const failureReply = {
            id: Date.now() + 3,
            sender: "NIA",
            text: resolveCompletionAssistantText({
              sessionCompleted: true,
              persistenceSucceeded: false,
              successText: successReply.text,
              correlationId: payload?.meta?.correlationId || "",
            }),
            createdAt: new Date().toISOString(),
          };
          const failureThread = [...base, failureReply];
          if (provisionalRecord) {
            persistSessionRecord({
              ...provisionalRecord,
              messages: toThreadMessages(failureThread),
              messageCount: failureThread.length,
              sessionComplete: false,
              completionReason: "",
              completionAction: "",
              completionVersion: null,
              endedAt: null,
              postSessionContinuation: null,
            }, { skipRemote: true });
          }
          saveSession(failureThread, {
            ...saveOverrides,
            continuationOffered: false,
            sessionComplete: false,
            completionReason: "",
            completionAction: "",
            completionVersion: null,
            endedAt: null,
          });
          setMessages(failureThread);
          latestMessagesRef.current = failureThread;
          setAnimatedAssistantMessageId(failureReply.id);
          setContinuationOffered(false);
        }
        setCompletionPersistence({
          status: completionPersisted ? "persisted" : "failed",
          versionId: completionPersisted ? completionVersion.id : "",
          lastError: completionPersisted
            ? ""
            : `Completion was not saved. Correlation ID: ${payload?.meta?.correlationId || "unavailable"}.`,
        });
        addPoints(1);
        openSupportReviewCase(supportReviewState);
        return;
      }

      setMessages(successfulThread);
      latestMessagesRef.current = successfulThread;
      setAnimatedAssistantMessageId(successReply.id);
      setContinuationOffered(nextContinuationOffered);
      addPoints(1);
      saveSession(successfulThread, saveOverrides, {
        skipSummaryRefresh: Boolean(sessionCompletion.completionVersion?.id),
      });
      openSupportReviewCase(supportReviewState);
    } catch (err) {
      console.error("chat send failed:", err);
      const fail = {
        id: Date.now() + 3,
        sender: "NIA",
        text: "I couldn’t reach the coaching engine just now. We can continue here, or try again in a moment.",
        createdAt: new Date().toISOString(),
      };
      const finalThread = [...base, fail];
      const supportReviewState = buildSupportReviewState();
      setMessages(finalThread);
      latestMessagesRef.current = finalThread;
      setAnimatedAssistantMessageId(fail.id);
      saveSession(finalThread, {
        followUp: continuedFollowUp || turnFollowUp,
        followUpRecommendation: turnFollowUpRecommendation,
        sessionSummary: turnSessionSummary,
        memberContinuedFollowUp,
        followUpCoachActionRequired,
        latestUserMessageText: trimmed,
        supportReview: supportReviewState,
      }, {
        skipSummaryRefresh: Boolean(sessionCompletion.completionVersion?.id),
      });
      openSupportReviewCase(supportReviewState);
    } finally {
      setLoading(false);
    }
  };

  // Open a previous session by id
  const applySessionRecord = (rec) => {
    if (!rec) return;
    const viewUser = rec.client || rec.user || username;
    setSessionId(String(rec.id));
    sessionStartRef.current = rec.createdAt || Date.now();
    if (rec.messages?.length) {
      setMessages(
        mapSessionMessagesForDisplay(rec.messages, {
          sessionId: rec.id,
          participantLabel: viewUser,
        })
      );
    }
    setNotes(rec.notes || { observations: "", reframes: "", next: "" });
    setAttachments(rec.attachments || []);
    setAssignments(rec.plan?.assignments || []);
    const activeSummary = resolveRecordSummaryForView(
      rec,
      recordMessagesForSummary(rec)
    );
    setSessionSummary(activeSummary);
    setSummaryDelivery(normalizeSummaryDelivery(rec.summaryDelivery));
    setPostSessionContinuation(rec.postSessionContinuation || null);
    setSessionCompletion(completionStateFromRecord(rec));
    setCompletionPersistence(completionPersistenceFromRecord(rec));
    setReturnSignalError("");
    setFollowUp(withSessionScope(buildSessionFollowUp(rec.followUp, activeSummary), rec.id));
    setFollowUpRecommendation(withSessionScope(buildSessionFollowUpRecommendation(rec.followUpRecommendation, activeSummary, rec.followUp), rec.id));
    setContinuationOffered(Boolean(rec.continuationOffered));
    setSessionPrivacy(normalizeSessionPrivacy(rec.privacy));
    setSessionSupportConsent(normalizeSupportConsent(rec.supportConsent));
    setSessionIdentity(
      role === "member"
        ? { ...memberIdentity, userEmail: user?.email || rec.userEmail || "" }
        : {
            client: rec.client || rec.user || username,
            user: rec.user || rec.client || username,
          userEmail: rec.userEmail || "",
          userId: rec.userId || "",
        }
    );
    setAnimatedAssistantMessageId(null);
    if (rec.rating) {
      setRatingData(rec.rating);
      setRatingSubmitted(true);
      setShowRating(false);
    }
  };
  const openById = (id) => {
    pendingSessionOriginRef.current = {
      sessionId: String(id),
      origin: SESSION_ORIGINS.HISTORY,
    };
    transitionSessionListener({
      type: "select_existing",
      origin: SESSION_ORIGINS.HISTORY,
      orgId,
      sessionId: id,
    });
    navigate(`/chat?id=${id}`);
  };

  const retryCompletionPersistence = useCallback(async () => {
    const activeSessionId = String(sessionId || sessionStartRef.current || "");
    const expectedVersionId = String(
      completionPersistence.versionId ||
      sessionCompletion.completionVersion?.id ||
      ""
    );
    if (!canPersistOwnedCompletion) {
      setCompletionPersistence({
        status: "failed",
        versionId: expectedVersionId,
        lastError:
          "Only the member who owns this session can persist its authoritative completion.",
      });
      return;
    }
    const record = findById(activeSessionId)?.rec || null;
    if (
      !activeSessionId ||
      !expectedVersionId ||
      String(record?.completionVersion?.id || "") !== expectedVersionId
    ) {
      setCompletionPersistence({
        status: "failed",
        versionId: expectedVersionId,
        lastError: "The exact local completion version is unavailable and was not regenerated.",
      });
      return;
    }
    setCompletionPersistence({
      status: "pending",
      versionId: expectedVersionId,
      lastError: "",
    });
    let retryInitialWriteOperationId = "";
    if (shouldStartInitialParentWrite(
      sessionListenerLifecycleRef.current,
      { orgId, sessionId: activeSessionId }
    )) {
      initialParentWriteCounterRef.current += 1;
      retryInitialWriteOperationId = [
        "initial-parent-completion-retry",
        `${orgId}:${activeSessionId}`,
        initialParentWriteCounterRef.current,
      ].join(":");
      transitionSessionListener({
        type: "initial_write_started",
        orgId,
        sessionId: activeSessionId,
        operationId: retryInitialWriteOperationId,
        writeKind: "completion_retry_transaction_parent_set",
      });
    }
    let completionPersisted = await writeSessionCompletionToFirestore(
      record,
      user,
      orgId
    );
    if (retryInitialWriteOperationId) {
      const serverReadable = completionPersisted &&
        await confirmOwnedSessionReadableFromServer(activeSessionId, user, orgId);
      completionPersisted = Boolean(completionPersisted && serverReadable);
      transitionSessionListener({
        type: completionPersisted
          ? "initial_write_confirmed"
          : "initial_write_failed",
        orgId,
        sessionId: activeSessionId,
        operationId: retryInitialWriteOperationId,
      });
    }
    setCompletionPersistence({
      status: completionPersisted ? "persisted" : "failed",
      versionId: expectedVersionId,
      lastError: completionPersisted
        ? ""
        : "The exact completion record could not be saved to shared storage.",
    });
  }, [
    canPersistOwnedCompletion,
    completionPersistence.versionId,
    findById,
    orgId,
    sessionCompletion.completionVersion?.id,
    sessionId,
    transitionSessionListener,
    user,
  ]);

  // Start a fresh session (new stable id)
  const startNewSession = async () => {
    if (
      canPersistOwnedCompletion &&
      completedSessionLocked &&
      sessionCompletion.completionVersion?.id &&
      completionPersistence.status !== "persisted"
    ) {
      setReturnSignalError(
        completionPersistence.status === "pending"
          ? "Nia is still saving the exact completion record. Please wait before starting a new session."
          : "The completion record is not yet confirmed in shared storage. Retry that save before starting a new session."
      );
      return;
    }
    if (
      role === "member" &&
      completedSessionLocked &&
      sessionCompletion.completionVersion?.id &&
      String(sessionId || sessionStartRef.current || "")
    ) {
      try {
        const recordReturn = httpsCallable(functionsClient, "recordSessionReturn");
        await recordReturn({
          orgId,
          sessionId: String(sessionId || sessionStartRef.current),
        });
      } catch {
        setReturnSignalError(
          "Nia could not safely record your return, so a new session was not started. Please try again."
        );
        return;
      }
    }
    sessionStartRef.current = null;
    transitionSessionListener({ type: "clear" });
    setSessionId(null);
    const greeting = [
      {
        id: Date.now(),
        sender: "NIA",
        text: "Hi, I’m Nia — your cognitive support coach. How can I support you today?",
        createdAt: new Date().toISOString(),
      },
    ];
    latestMessagesRef.current = greeting;
    setMessages(greeting);
    setNotes({ observations: "", reframes: "", next: "" });
    setAttachments([]);
    setAssignments([]);
    setSessionSummary(null);
    setSummaryDelivery(normalizeSummaryDelivery(null));
    setPostSessionContinuation(null);
    setSessionCompletion(completionStateFromRecord());
    setCompletionPersistence(completionPersistenceFromRecord());
    setReturnSignalError("");
    setFollowUp(null);
    setFollowUpRecommendation(null);
    setContinuationOffered(false);
    setSessionPrivacy(normalizeSessionPrivacy(null));
    setSessionSupportConsent(normalizeSupportConsent(null));
    setSessionIdentity(memberIdentity);
    setAnimatedAssistantMessageId(null);
    setInput("");
    setShowRating(false);
    setRatingScore(0);
    setRatingFeedback("");
    setRatingSubmitted(false);
    setRatingDismissed(false);
    setRatingData(null);
    navigate("/chat");
  };

  const submitRating = async () => {
    if (!ratingScore) return;
    const key = ratingKey;
    const payload = {
      score: ratingScore,
      feedback: ratingFeedback.trim(),
      createdAt: Date.now(),
    };
    try {
      if (key) localStorage.setItem(key, JSON.stringify(payload));
    } catch {}
    setRatingSubmitted(true);
    setShowRating(false);
    setRatingData(payload);
    saveSession(messages);
  };

  const authoritativeCompletedSessionRecord = useMemo(
    () => (
      selectedLiveSessionRecord?.sessionComplete === true &&
      selectedLiveSessionRecord?.completionVersion?.id
        ? selectedLiveSessionRecord
        : null
    ),
    [selectedLiveSessionRecord]
  );
  const messagesForView = useMemo(() => {
    if (!authoritativeCompletedSessionRecord?.messages?.length) return messages;
    return mapSessionMessagesForDisplay(authoritativeCompletedSessionRecord.messages, {
      sessionId: authoritativeCompletedSessionRecord.id,
      participantLabel:
        authoritativeCompletedSessionRecord.client ||
        authoritativeCompletedSessionRecord.user ||
        username,
    });
  }, [authoritativeCompletedSessionRecord, messages, username]);
  const interventionCaseType = String(activeInterventionCase?.caseType || "flagged");
  const threadMessagesForSummary = useMemo(
    () => (
      authoritativeCompletedSessionRecord
        ? recordMessagesForSummary(authoritativeCompletedSessionRecord)
        : toThreadMessages(messages)
    ),
    [authoritativeCompletedSessionRecord, messages, recordMessagesForSummary, toThreadMessages]
  );
  const activeInterventionCaseOpen = Boolean(
    activeInterventionCase?.id && !isFinalFlagStatus(activeInterventionCase?.status)
  );
  const activeSessionKeyForFlags = sessionId || sessionStartRef.current || "";
  const scopedFlagSummary = getSessionScopedValue(sessionSummary, activeSessionKeyForFlags);
  const actionableFlagSuggested = hasActionableSessionFlagSuggestion({
    messages: threadMessagesForSummary,
    sessionSummary: scopedFlagSummary,
  });
  const showFlagFollowUpPanel = canUseCoachTools && Boolean(sessionId) && Boolean(
    activeInterventionCaseOpen || actionableFlagSuggested
  );
  const caseClaimedByOther = Boolean(
    activeInterventionCase?.claimedBy && user?.uid && String(activeInterventionCase.claimedBy) !== String(user.uid)
  );
  const canClaimCase = Boolean(
    showFlagFollowUpPanel && (
      !activeInterventionCase ||
      (
        ["open", "escalated"].includes(String(activeInterventionCase?.status || "").toLowerCase()) &&
        !caseClaimedByOther
      )
    )
  );
  const canResolveCase = Boolean(
    showFlagFollowUpPanel && (
      !activeInterventionCase ||
      (
        activeInterventionCaseOpen &&
        (
          role === "superadmin" ||
          !activeInterventionCase?.claimedBy ||
          String(activeInterventionCase.claimedBy) === String(user?.uid)
        )
      )
    )
  );
  const canRecordCoachContactOnCase = Boolean(canUseCoachTools && activeInterventionCaseOpen);
  const fallbackSessionSummary = useMemo(
    () => withSessionScope(
      buildStructuredFallbackSummary(threadMessagesForSummary, {
        followUp: getSessionScopedValue(followUp, sessionId || sessionStartRef.current || ""),
      }),
      sessionId || sessionStartRef.current || ""
    ),
    [followUp, sessionId, threadMessagesForSummary]
  );
  const displayedSessionSummary = useMemo(
    () => resolveActiveSessionSummary({
      sessionSummary,
      fallbackSummary: fallbackSessionSummary,
      sessionId: sessionId || sessionStartRef.current || "",
      messages: threadMessagesForSummary,
      followUp: getSessionScopedValue(followUp, sessionId || sessionStartRef.current || ""),
    }),
    [followUp, sessionId, sessionSummary, fallbackSessionSummary, threadMessagesForSummary]
  );
  const scopedPostSessionSummary = useMemo(
    () => getSessionScopedValue(
      selectedLiveSessionRecord?.sessionSummary || sessionSummary,
      sessionId || sessionStartRef.current || ""
    ),
    [selectedLiveSessionRecord, sessionId, sessionSummary]
  );
  const activePostSessionRecord = useMemo(
    () => selectSessionRecordForView({
      liveRecord: selectedLiveSessionRecord,
      fallbackRecord: {
        id: String(sessionId || sessionStartRef.current || ""),
        orgId,
        userId:
          sessionIdentity.userId ||
          (role === "member" ? user?.uid || "" : ""),
        messages: threadMessagesForSummary,
        messageCount: threadMessagesForSummary.length,
        sessionComplete: sessionCompletion.sessionComplete === true,
        endedAt: sessionCompletion.endedAt || null,
        completionVersion: sessionCompletion.completionVersion,
        sessionSummary: scopedPostSessionSummary,
        postSessionContinuation,
        summaryDelivery,
      },
      sessionId: sessionId || sessionStartRef.current || "",
    }),
    [
      orgId,
      postSessionContinuation,
      role,
      selectedLiveSessionRecord,
      sessionCompletion.completionVersion,
      sessionCompletion.endedAt,
      sessionCompletion.sessionComplete,
      sessionId,
      sessionIdentity.userId,
      scopedPostSessionSummary,
      summaryDelivery,
      threadMessagesForSummary,
      user?.uid,
    ]
  );
  const activeSummaryState = useMemo(
    () => getTrustedActiveSummaryState(activePostSessionRecord),
    [activePostSessionRecord]
  );
  const visibleSessionSummary = useMemo(
    () => {
      if (hasTrustedFinalSummaryProvenance(activePostSessionRecord)) {
        return scopedPostSessionSummary;
      }
      if (activeSummaryState.snapshotValid) {
        return activeSummaryState.summary || scopedPostSessionSummary;
      }
      return resolveDisplaySessionSummary(
        displayedSessionSummary,
        sessionId || sessionStartRef.current || "",
        threadMessagesForSummary,
        getSessionScopedValue(followUp, sessionId || sessionStartRef.current || "")
      );
    },
    [
      activePostSessionRecord,
      activeSummaryState,
      displayedSessionSummary,
      followUp,
      scopedPostSessionSummary,
      sessionId,
      threadMessagesForSummary,
    ]
  );
  const scopedLiveFollowUp = useMemo(
    () => getSessionScopedValue(
      authoritativeCompletedSessionRecord?.followUp || followUp,
      sessionId || sessionStartRef.current || ""
    ),
    [authoritativeCompletedSessionRecord, followUp, sessionId]
  );
  const displayedFollowUp = useMemo(
    () => withSessionScope(resolveDisplaySessionFollowUp(
      scopedLiveFollowUp,
      visibleSessionSummary,
      { messages: threadMessagesForSummary }
    ), sessionId || sessionStartRef.current || ""),
    [scopedLiveFollowUp, visibleSessionSummary, sessionId, threadMessagesForSummary]
  );
  const displayedFollowUpDisplayStatus = useMemo(
    () => getFollowUpDisplayStatus(displayedFollowUp),
    [displayedFollowUp]
  );
  const scopedLiveFollowUpRecommendation = useMemo(
    () => getSessionScopedValue(
      authoritativeCompletedSessionRecord?.followUpRecommendation || followUpRecommendation,
      sessionId || sessionStartRef.current || ""
    ),
    [authoritativeCompletedSessionRecord, followUpRecommendation, sessionId]
  );
  const displayedFollowUpRecommendation = useMemo(
    () => withSessionScope(
      buildSessionFollowUpRecommendation(
        scopedLiveFollowUpRecommendation,
        visibleSessionSummary,
        displayedFollowUp,
        { messages: threadMessagesForSummary }
      ),
      sessionId || sessionStartRef.current || ""
    ),
    [scopedLiveFollowUpRecommendation, displayedFollowUp, visibleSessionSummary, sessionId, threadMessagesForSummary]
  );
  const sessionViewRecord = useMemo(
    () => ({
      id: sessionId || sessionStartRef.current || Date.now(),
      createdAt:
        authoritativeCompletedSessionRecord?.createdAt ||
        sessionStartRef.current ||
        Date.now(),
      client:
        authoritativeCompletedSessionRecord?.client ||
        sessionIdentity.client ||
        username,
      user:
        authoritativeCompletedSessionRecord?.user ||
        sessionIdentity.user ||
        username,
      userEmail:
        authoritativeCompletedSessionRecord?.userEmail ||
        sessionIdentity.userEmail ||
        user?.email ||
        "",
      userId:
        authoritativeCompletedSessionRecord?.userId ||
        sessionIdentity.userId ||
        (role === "member" ? user?.uid || "" : ""),
      anonymousLabel: getAnonymousSessionLabel({
        id: sessionId || sessionStartRef.current || Date.now(),
        createdAt:
          authoritativeCompletedSessionRecord?.createdAt ||
          sessionStartRef.current ||
          Date.now(),
        userId:
          authoritativeCompletedSessionRecord?.userId ||
          sessionIdentity.userId ||
          (role === "member" ? user?.uid || "" : ""),
      }),
      privacy: authoritativeCompletedSessionRecord?.privacy || sessionPrivacy,
      supportConsent:
        authoritativeCompletedSessionRecord?.supportConsent || sessionSupportConsent,
    }),
    [authoritativeCompletedSessionRecord, sessionId, sessionIdentity.client, sessionIdentity.user, sessionIdentity.userEmail, sessionIdentity.userId, role, sessionPrivacy, sessionSupportConsent, user?.email, user?.uid, username]
  );
  const sessionOwnedByViewer = useMemo(
    () => isOwnSession(sessionViewRecord, user?.uid || ""),
    [sessionViewRecord, user?.uid]
  );
  const sessionAudience = useMemo(
    () => (sessionOwnedByViewer || !sessionId ? "self" : "reviewer"),
    [sessionOwnedByViewer, sessionId]
  );
  const continuationView = useMemo(
    () => normalizePostSessionContinuation({
      ...activePostSessionRecord,
      sessionSummary: visibleSessionSummary,
      summaryDelivery: normalizeSummaryDelivery(
        activePostSessionRecord?.summaryDelivery || summaryDelivery
      ),
      followUp: displayedFollowUp,
    }),
    [
      activePostSessionRecord,
      displayedFollowUp,
      summaryDelivery,
      visibleSessionSummary,
    ]
  );
  const finalizationDisplayState = getPostSessionFinalizationDisplayState(
    continuationView
  );
  const finalizationDisplayPending = finalizationDisplayState === "pending";
  const finalizationDisplayFailed = finalizationDisplayState === "failed";
  const finalizationDisplayUnverified = finalizationDisplayState === "unverified";
  const finalizationDetailsVisible =
    canDisplayPostSessionFinalSummaryDetails(continuationView);
  const activeSummaryDetailsVisible = Boolean(
    sessionCompletion.sessionComplete !== true &&
    activeSummaryState.snapshotValid
  );
  const sessionSummaryDetailsVisible = Boolean(
    finalizationDetailsVisible || activeSummaryDetailsVisible
  );
  const maskedSessionSummaryText = useMemo(
    () => {
      if (sessionCompletion.sessionComplete !== true) {
        if (activeSummaryState.snapshotValid) {
          const audienceText = formatSummaryTextForAudience(
            visibleSessionSummary.summaryText,
            sessionAudience
          );
          return getSessionDisplayText(audienceText, sessionViewRecord, role, {
            viewerUserId: user?.uid || "",
            viewerRealName: displayName,
          });
        }
        if (activeSummaryState.state === "failed") {
          return "Summary temporarily unavailable.";
        }
        if (activeSummaryState.state === "updating") {
          return "Summary is being prepared.";
        }
        return "Summary not ready.";
      }
      if (finalizationDisplayPending) {
        return "Final summary is being prepared.";
      }
      if (finalizationDisplayUnverified) {
        return "Final summary status is unverified.";
      }
      if (finalizationDisplayFailed) {
        return "A dependable final summary is unavailable. Automatic email and reminder continuation will not start from fallback content.";
      }
      if (!finalizationDetailsVisible) {
        return "A final summary has not been prepared.";
      }
      const audienceText = formatSummaryTextForAudience(visibleSessionSummary.summaryText, sessionAudience);
      return getSessionDisplayText(audienceText, sessionViewRecord, role, {
        viewerUserId: user?.uid || "",
        viewerRealName: displayName,
      });
    },
    [
      displayName,
      activeSummaryState,
      finalizationDetailsVisible,
      finalizationDisplayFailed,
      finalizationDisplayPending,
      finalizationDisplayUnverified,
      role,
      sessionAudience,
      sessionCompletion.sessionComplete,
      sessionViewRecord,
      user?.uid,
      visibleSessionSummary.summaryText,
    ]
  );
  const memberSafeSuggestedNextStep = useMemo(
    () => formatSupportTextForAudience(visibleSessionSummary.suggestedNextStep, sessionAudience),
    [visibleSessionSummary.suggestedNextStep, sessionAudience]
  );
  const summaryGenerationLabel = sessionCompletion.sessionComplete === true
    ? getPostSessionFinalizationLabel(continuationView)
    : activeSummaryState.state === "current"
      ? "Current session summary"
      : activeSummaryState.state === "updating"
        ? "Updating summary"
        : activeSummaryState.state === "stale"
          ? "Earlier session summary"
          : activeSummaryState.state === "failed"
            ? "Summary temporarily unavailable"
            : "Summary not ready";
  const activeSummaryNotice = sessionCompletion.sessionComplete === true
    ? ""
    : activeSummaryState.state === "current"
      ? "You can keep talking; this summary may update."
      : activeSummaryState.state === "updating" && activeSummaryState.snapshotValid
        ? "Updating summary. Based on earlier messages."
        : activeSummaryState.state === "stale"
          ? "Based on earlier messages."
          : activeSummaryState.state === "failed" && activeSummaryState.snapshotValid
            ? "The latest update failed. Based on earlier messages."
            : "";
  const summaryDeliveryLabel = getPostSessionAutomaticEmailLabel(continuationView);
  const summaryManualResendEligibility = getPostSessionManualResendEligibility(
    continuationView
  );
  const summaryManualResendLabel = getPostSessionManualResendLabel(continuationView);
  const summaryReminderLabel = getPostSessionReminderLabel(continuationView);
  useEffect(() => {
    recordRuntimeDiagnostic("chat.rendered-post-session-projection", {
      renderCount: renderCountRef.current,
      selectedSessionId: sessionId,
      selectedSessionIdType: typeof sessionId,
      liveSessionRecord: describeRuntimeSessionRecord(selectedLiveSessionRecord),
      localCompletion: {
        sessionComplete: sessionCompletion.sessionComplete === true,
        endedAt: sessionCompletion.endedAt || null,
        completionVersionId: String(
          sessionCompletion.completionVersion?.id || ""
        ),
      },
      summaryPanelRecord: describeRuntimeSessionRecord(activePostSessionRecord),
      summaryPanelObject: visibleSessionSummary
        ? {
            generationStatus: String(
              visibleSessionSummary.generationStatus || ""
            ),
            status: String(visibleSessionSummary.status || ""),
            canonical: visibleSessionSummary.canonical === true,
            isCurrent: visibleSessionSummary.isCurrent === true,
            completionVersionId: String(
              visibleSessionSummary.completionVersionId || ""
            ),
            summaryVersion: String(visibleSessionSummary.summaryVersion || ""),
            sourceTranscriptFingerprint: String(
              visibleSessionSummary.sourceTranscriptFingerprint || ""
            ),
          }
        : null,
      emailStatusObject: {
        status: String(continuationView.automaticEmail?.status || ""),
        relayStatus: String(
          continuationView.automaticEmail?.relayStatus || ""
        ),
        completionVersionId: String(
          continuationView.automaticEmail?.completionVersionId || ""
        ),
        summaryVersion: String(
          continuationView.automaticEmail?.summaryVersion || ""
        ),
      },
      reminderStatusObject: {
        status: String(continuationView.reminder?.status || ""),
        completionVersionId: String(
          continuationView.reminder?.completionVersionId || ""
        ),
        summaryVersion: String(
          continuationView.reminder?.summaryVersion || ""
        ),
      },
      rendered: {
        summaryGenerationLabel,
        summaryTextLength: String(maskedSessionSummaryText || "").length,
        automaticEmailLabel: summaryDeliveryLabel,
        manualResendLabel: summaryManualResendLabel,
        reminderLabel: summaryReminderLabel,
        transcriptLocked: completedSessionLocked,
      },
    });
  });
  const memberSafeFollowUpText = useMemo(
    () => formatSupportTextForAudience(displayedFollowUp?.nextStepText || "", sessionAudience),
    [displayedFollowUp?.nextStepText, sessionAudience]
  );
  const memberSafeRecommendationText = useMemo(
    () => formatSupportTextForAudience(displayedFollowUpRecommendation?.recommendationText || "", sessionAudience),
    [displayedFollowUpRecommendation?.recommendationText, sessionAudience]
  );
  const privacyStatus = useMemo(
    () => getSessionPrivacyStatus(sessionViewRecord),
    [sessionViewRecord]
  );
  const supportConsentStatus = useMemo(
    () => getSupportConsentStatus(sessionViewRecord),
    [sessionViewRecord]
  );
  const viewerSessionDisplayName = useMemo(
    () => getViewerSessionDisplayName(sessionViewRecord, role, {
      viewerUserId: user?.uid || "",
      viewerRealName: displayName,
    }),
    [displayName, role, sessionViewRecord, user?.uid]
  );
  const renderedMessages = useMemo(
    () => messagesForView.map((message) => ({
      ...message,
      text: getRenderedSessionMessage(message, sessionViewRecord, role, {
        viewerUserId: user?.uid || "",
        viewerRealName: displayName,
      }),
    })),
    [displayName, messagesForView, role, sessionViewRecord, user?.uid]
  );
  const showPrivacyPrompt = role === "member" && messagesForView.length > 1 && privacyStatus.identityMode === "anonymous" && privacyStatus.setBy === "system";
  const showSupportDecision = role === "member" && !!latestCoachMessage && supportConsentStatus.directCoachContact === "not_requested";
  const showSupportPathCard = Boolean(
    latestCoachMessage ||
    supportConsentStatus.directCoachContact === "requested" ||
    supportConsentStatus.directCoachContact === "allowed" ||
    supportConsentStatus.directCoachContact === "declined"
  );
  const updateSessionPrivacy = useCallback((identityMode, setBy = "user") => {
    const nextPrivacy = normalizeSessionPrivacy({
      identityMode,
      setBy,
      updatedAt: Date.now(),
    });
    const nextSupportConsent =
      identityMode === "anonymous" && sessionSupportConsent?.directCoachContact === "allowed"
        ? normalizeSupportConsent({
            directCoachContact: "declined",
            setBy: "user",
            updatedAt: Date.now(),
          })
        : sessionSupportConsent;
    setSessionPrivacy(nextPrivacy);
    setSessionSupportConsent(nextSupportConsent);
    if (role === "member") {
      setSessionIdentity(memberIdentity);
    }
    if (sessionId || messages.length > 1) {
      saveSession(messages, {
        privacy: nextPrivacy,
        supportConsent: nextSupportConsent,
        client: memberIdentity.client,
        user: memberIdentity.user,
        sessionSummary: displayedSessionSummary,
      }, {
        skipSummaryRefresh: completedSessionLocked,
      });
    }
  }, [completedSessionLocked, displayedSessionSummary, memberIdentity, messages, role, saveSession, sessionId, sessionSupportConsent]);
  const updateSupportDecision = useCallback(({ directCoachContact, identityMode = null }) => {
    const nextSupportConsent = normalizeSupportConsent({
      directCoachContact,
      setBy: "user",
      updatedAt: Date.now(),
    });
    const nextPrivacy = identityMode
      ? normalizeSessionPrivacy({
          identityMode,
          setBy: "user",
          updatedAt: Date.now(),
        })
      : sessionPrivacy;
    setSessionSupportConsent(nextSupportConsent);
    if (identityMode) {
      setSessionPrivacy(nextPrivacy);
    }
    if (role === "member") {
      setSessionIdentity(memberIdentity);
    }
    if (sessionId || messages.length > 1) {
      saveSession(messages, {
        supportConsent: nextSupportConsent,
        privacy: nextPrivacy,
        client: memberIdentity.client,
        user: memberIdentity.user,
        sessionSummary: displayedSessionSummary,
      }, {
        skipSummaryRefresh: completedSessionLocked,
      });
    }
  }, [completedSessionLocked, displayedSessionSummary, memberIdentity, messages, role, saveSession, sessionId, sessionPrivacy]);
  const showResumeFollowUpBanner = role === "member" && resumeFollowUp && displayedFollowUpDisplayStatus === "resumed";
  const coachMessagingTitle = canUseCoachTools ? "Coach Intervention Messages" : "Coach Messages";
  const coachMessagingIntro = canUseCoachTools
    ? "Private coach-to-member thread for this session."
    : "Private coaching thread for this session.";
  const coachMessagingPlaceholder = isReadOnly
    ? "Investor access is read-only."
    : canUseCoachTools
      ? "Message the member..."
      : "Message the coach...";

  const handleCoachSend = useCallback(async () => {
    if (!coachInput.trim() || !sessionId) return;
    const orgId = activeOrgId || user?.orgId || "org_default";
    setCoachSending(true);
    try {
      await sendCoachThreadMessage(user, orgId, sessionId, coachInput.trim());
      if (canRecordCoachContactOnCase) {
        const result = await recordFlagCaseCoachContactBySession(user, orgId, sessionId, {
          caseId: activeInterventionCase?.id,
        });
        if (result?.ok) {
          setActiveInterventionCase((prev) => (
            prev
              ? {
                  ...prev,
                  status: result.status || "coach_message_sent",
                  claimedBy: prev.claimedBy || user?.uid || "",
                  claimedByEmail: prev.claimedByEmail || user?.email || "",
                  claimedAt: prev.claimedAt || Date.now(),
                  coachContactedAt: prev.coachContactedAt || Date.now(),
                  lastCoachContactAt: Date.now(),
                  updatedAt: Date.now(),
                }
              : prev
          ));
          refreshActiveFlagCase().catch(() => {});
        }
      }
      setCoachInput("");
    } finally {
      setCoachSending(false);
    }
  }, [activeInterventionCase?.id, activeOrgId, canRecordCoachContactOnCase, coachInput, refreshActiveFlagCase, sessionId, user]);
  const sessionLabel = viewerSessionDisplayName;
  const sessionMeta = sessionId
    ? [
        getSessionLifecycleLabel(activePostSessionRecord),
        sessionOwnedByViewer ? "Your session" : viewerSessionDisplayName,
        sessionStartRef.current ? new Date(sessionStartRef.current).toLocaleString() : null,
        `${messagesForView.length} message${messagesForView.length === 1 ? "" : "s"}`,
      ].filter(Boolean).join(" • ")
    : "New conversation";
  const goBackTarget = getChatExitTarget(role);
  const coachInterruptComposerAlert = showCoachInterrupt ? (
    <div className={`focused-chat-coach-interrupt focused-chat-coach-interrupt--composer${coachUnread > 0 ? " is-unread" : ""}`}>
      <div className="focused-chat-coach-interrupt__copy">
        <strong>{coachMessageCount <= 1 ? "Your coach has joined the session" : "New coach message"}</strong>
        <span>{latestCoachMessage?.text || ""}</span>
      </div>
      <div className="focused-chat-coach-interrupt__actions">
        <button type="button" className="btn btn-primary" onClick={() => setShowCoachPanel(true)}>
          Open coach messages
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            if (coachReadKey && latestCoachMessageAt) {
              markCoachThreadRead(user?.uid, sessionId, latestCoachMessageAt);
            }
            setDismissedCoachMessageAt(latestCoachMessageAt);
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className={`chat-main focused-chat-layout ${showHistory ? "with-history" : ""}`} aria-busy={loading}>
      <div className="chat-wrap focused-chat-wrap" ref={chatViewportRef}>
        <FocusedChatSurface
          sessionLabel={sessionLabel}
          sessionMeta={sessionMeta}
          onGoBack={() => {
            navigate(goBackTarget, { replace: true });
          }}
          detailsOpen={showHistory}
          onToggleDetails={() => setShowHistory((s) => !s)}
          onStartNewSession={startNewSession}
          animatedAssistantMessageId={animatedAssistantMessageId}
          messages={renderedMessages}
          loading={loading}
          showTimestamps={SHOW_TS()}
          participantLabel={viewerSessionDisplayName}
          chatBoxRef={chatBoxRef}
          input={input}
          onInputChange={setInput}
          onInputKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!loading && !isReadOnly && !completedSessionLocked) handleSend();
            }
          }}
          onSend={handleSend}
          sendDisabled={loading || isReadOnly || completedSessionLocked}
          inputPlaceholder={
            isReadOnly
              ? "Investor access is read-only."
              : completedSessionLocked
                ? "This session is complete. Start a new session to continue."
                : loading
                  ? "Sending..."
                  : "Type your message..."
          }
          readOnlyHint={isReadOnly ? "Investor access is read-only." : ""}
          composerAlert={
            completedSessionLocked
              ? (
                  canPersistOwnedCompletion &&
                  versionedCompletionLocked &&
                  completionPersistence.status !== "persisted"
                )
                ? "This completed transcript is locked while its exact completion record is confirmed in shared storage."
                : "This completed transcript is locked while final summary processing completes. Use New to continue in a fresh session."
              : coachInterruptComposerAlert
          }
        >
          {canPersistOwnedCompletion &&
          versionedCompletionLocked &&
          completionPersistence.status !== "persisted" ? (
            <div className="focused-chat-banner" role="alert">
              <strong>
                {completionPersistence.status === "pending"
                  ? "Saving completion"
                  : "Completion save needs attention"}
              </strong>
              <div className="nia-muted">
                {completionPersistence.status === "pending"
                  ? "Nia is saving this exact completion version before final summary and email processing can begin."
                  : completionPersistence.lastError ||
                    "Shared storage has not yet confirmed this exact completion version."}
              </div>
              {["failed", "unverified"].includes(completionPersistence.status) ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={retryCompletionPersistence}
                  style={{ marginTop: 8 }}
                >
                  Retry exact completion save
                </button>
              ) : null}
            </div>
          ) : null}

          {returnSignalError ? (
            <div className="focused-chat-banner" role="alert">
              <strong>Return not recorded</strong>
              <div className="nia-muted">{returnSignalError}</div>
              {postSessionReturnTarget ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setReturnSignalRetryNonce((value) => value + 1)}
                  style={{ marginTop: 8 }}
                >
                  Retry secure return
                </button>
              ) : null}
            </div>
          ) : null}

          {showResumeFollowUpBanner ? (
            <div className="focused-chat-banner">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <strong>Your follow-up is in progress</strong>
                <span className="nia-pill">RESUMED</span>
              </div>
              <div className="nia-muted">
                Scheduled for {displayedFollowUp?.scheduledFor ? formatFollowUpDate(displayedFollowUp.scheduledFor) : "soon"}
              </div>
              <div>{memberSafeFollowUpText}</div>
            </div>
          ) : null}

          {role === "member" && displayedFollowUp ? (
            <div className="focused-chat-followup-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <strong>Next step</strong>
                <span className="nia-pill">{String(displayedFollowUpDisplayStatus || displayedFollowUp.status || "suggested").toUpperCase()}</span>
              </div>
              <div style={{ marginTop: 8 }}>{memberSafeFollowUpText}</div>
              {displayedFollowUp.scheduledFor ? (
                <div className="nia-muted" style={{ marginTop: 8 }}>
                  {displayedFollowUpDisplayStatus === "scheduled" || displayedFollowUpDisplayStatus === "resumed"
                    ? `Scheduled for ${formatFollowUpDate(displayedFollowUp.scheduledFor)}`
                    : `Originally scheduled for ${formatFollowUpDate(displayedFollowUp.scheduledFor)}`}
                </div>
              ) : null}
            </div>
          ) : null}

          {showPrivacyPrompt ? (
            <div className="focused-chat-banner">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <strong>Keep this session anonymous?</strong>
                <span className="nia-pill">Default</span>
              </div>
              <div className="nia-muted">
                Your coach can review the conversation to support you, but your identity stays hidden unless you choose to share it.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <button type="button" className="btn btn-ghost" onClick={() => updateSessionPrivacy("anonymous", "user")}>
                  Keep anonymous
                </button>
                <button type="button" className="btn btn-primary" onClick={() => updateSessionPrivacy("identified", "user")}>
                  Share my identity
                </button>
              </div>
            </div>
          ) : null}

          {showSupportDecision ? (
            <div className="focused-chat-banner focused-chat-support-decision">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <strong>Your coach is available to support you</strong>
                <span className="nia-pill">Choose</span>
              </div>
              <div className="nia-muted">
                Choose how you’d like to continue.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => updateSupportDecision({ directCoachContact: "declined", identityMode: "anonymous" })}
                >
                  Continue anonymously
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => updateSupportDecision({ directCoachContact: "allowed", identityMode: "identified" })}
                >
                  Share my identity
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => updateSupportDecision({ directCoachContact: "requested" })}
                >
                  Request 1-on-1 support
                </button>
              </div>
            </div>
          ) : null}
        </FocusedChatSurface>
      </div>

      <ChatDetailsPanel
        open={showHistory}
        onClose={() => setShowHistory(false)}
        title={canUseCoachTools ? "Session workspace" : "Your session"}
        subtitle={canUseCoachTools
          ? "History, summary, follow-up, and coach tools for the active session."
          : "History, summary, and follow-up for this conversation."}
      >
        <Card className="chat-details-card chat-details-card--priority" title="Privacy">
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <strong>{privacyStatus.label}</strong>
              <span className="nia-pill">{privacyStatus.identityMode === "identified" ? "Identified" : "Anonymous"}</span>
            </div>
            <div className="nia-muted">{privacyStatus.detail}</div>
            <div className="nia-muted">Display name: {viewerSessionDisplayName}</div>
            {(role === "coach" || role === "superadmin" || role === "investor") ? (
              <div className="nia-muted">Review label: {viewerSessionDisplayName}</div>
            ) : null}
            {role === "member" ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {privacyStatus.identityMode !== "identified" ? (
                  <button type="button" className="btn btn-primary" onClick={() => updateSessionPrivacy("identified", "user")} disabled={messages.length <= 1}>
                    Share identity with coach
                  </button>
                ) : (
                  <button type="button" className="btn btn-ghost" onClick={() => updateSessionPrivacy("anonymous", "user")} disabled={messages.length <= 1}>
                    Hide identity again
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </Card>

        {showSupportPathCard ? (
        <Card className="chat-details-card chat-details-card--priority" title="Support Path">
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <strong>{supportConsentStatus.label}</strong>
              <span className="nia-pill">
                {supportConsentStatus.directCoachContact === "requested"
                  ? "Requested"
                  : supportConsentStatus.directCoachContact === "allowed"
                    ? "Identified"
                    : supportConsentStatus.directCoachContact === "declined"
                      ? "Anonymous"
                      : "Pending"}
              </span>
            </div>
            <div className="nia-muted">{supportConsentStatus.detail}</div>
            {(role === "coach" || role === "superadmin") && supportConsentStatus.directCoachContact === "requested" ? (
              <div className="nia-muted">Member requested 1-on-1 support for this session. Booking flow is not enabled yet.</div>
            ) : null}
          </div>
        </Card>
        ) : null}

        <Card className="history-panel chat-details-card" title="History">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div className="nia-subtitle">Recent sessions</div>
            <button className="btn btn-ghost" onClick={startNewSession} title="Start a fresh conversation">
              New chat
            </button>
          </div>
          {sessions.length === 0 ? (
            <div className="nia-muted">No saved sessions yet.</div>
          ) : (
            <div className="nia-list">
              {visibleHistorySessions.map((s) => {
                const currentSummary = resolveCurrentRecordSummary(s);
                return (
                  <button
                  key={s.id}
                  type="button"
                  className={`nia-list-btn nia-history-item${String(s.id) === String(sessionId) ? " is-active" : ""}`}
                  aria-current={String(s.id) === String(sessionId) ? "page" : undefined}
                  onClick={() => {
                    applySessionRecord(s);
                    openById(s.id);
                    scrollToChatViewport();
                    if (typeof window !== "undefined" && window.innerWidth <= 960) {
                      setShowHistory(false);
                    }
                  }}
                >
                  <span className="title">
                    {getSessionDisplayText(s.title || s.issueSummary || "Untitled conversation", s, role, {
                      viewerUserId: user?.uid || "",
                      viewerRealName: displayName,
                    })}
                  </span>
                  <span className="nia-pill">{new Date(s.createdAt).toLocaleDateString()}</span>
                  <span className="nia-muted" style={{ width: "100%", textAlign: "left" }}>
                    {getViewerSessionDisplayName(s, role, {
                      viewerUserId: user?.uid || "",
                      viewerRealName: displayName,
                    })}
                  </span>
                  {currentSummary?.summaryText ? (
                    <span className="nia-muted" style={{ width: "100%", textAlign: "left" }}>
                      {getSessionDisplayText(
                        formatSummarySnippetForAudience(
                          String(currentSummary.summaryText),
                          isOwnSession(s, user?.uid || "") || role === "member" ? "self" : "reviewer",
                          120
                        ),
                        s,
                        role,
                        {
                        viewerUserId: user?.uid || "",
                        viewerRealName: displayName,
                        }
                      )}
                    </span>
                  ) : null}
                  </button>
                );
              })}
              {historyNeedsExpansion ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  aria-expanded={historyExpanded}
                  onClick={() => setHistoryExpanded((expanded) => !expanded)}
                  style={{ width: "100%", justifyContent: "center", marginTop: 2 }}
                >
                  {historyExpanded
                    ? "Show fewer sessions"
                    : `View older sessions${hiddenHistoryCount ? ` (${hiddenHistoryCount})` : ""}`}
                </button>
              ) : null}
            </div>
          )}
        </Card>

        {sessionId && !isReadOnly ? (
          <Card className="chat-details-card" title="Coach Messages">
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <strong>{activeCoachThread.length ? "Live thread connected" : "No coach messages yet"}</strong>
                {coachUnread > 0 ? <span className="nia-pill">{coachUnread} new</span> : null}
              </div>
              <div className="nia-muted">
                {latestCoachMessage
                  ? `Latest from Coach: ${latestCoachMessage.text}`
                  : "No coach messages yet."}
              </div>
              {canUseCoachTools ? (
                <>
                  <div className="chat-box coach-box" style={{ maxHeight: 220 }}>
                    {activeCoachThread.length ? (
                      activeCoachThread.map((msg, i) => {
                        const label = getCoachThreadSenderLabel(msg, sessionViewRecord, role);
                        return (
                          <div key={`${msg.createdAt || i}-${i}`} className={`chat-row ${label === "Coach" ? "assistant" : "user"}`}>
                            <div className={`bubble ${label === "Coach" ? "assistant" : "user"}`}>
                              <strong>{label}:</strong> {msg.text}
                              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                                {msg.createdAt ? new Date(msg.createdAt).toLocaleString() : ""}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="nia-muted">No coach messages yet.</div>
                    )}
                  </div>
                  <textarea
                    rows="2"
                    className="chat-input"
                    value={coachInput}
                    disabled={coachSending || isReadOnly}
                    onChange={(e) => setCoachInput(e.target.value)}
                    placeholder={coachMessagingPlaceholder}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" className="btn btn-ghost" onClick={() => setShowCoachPanel(true)}>
                      Open full coach thread
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={isReadOnly || coachSending || !coachInput.trim() || !sessionId}
                      onClick={handleCoachSend}
                    >
                      {coachSending ? "Sending…" : "Send message"}
                    </button>
                  </div>
                </>
              ) : (
                <div>
                  <button type="button" className="btn btn-ghost" onClick={() => setShowCoachPanel(true)}>
                    {activeCoachThread.length ? "Open coach messages" : "Open coach message panel"}
                  </button>
                </div>
              )}
            </div>
          </Card>
        ) : null}

        {showFlagFollowUpPanel ? (
          <Card className="chat-details-card" title="Coach Intervention">
            <div style={{ display: "grid", gap: 8 }}>
              {activeInterventionCase ? (
                <div className="nia-muted">
                  {interventionCaseType === "session_intervention" ? "Session intervention" : "Flag follow-up"}
                  : {activeInterventionCase.id} • Status: {getFlagCaseStatusLabel(activeInterventionCase)}
                  {activeInterventionCase.claimedByEmail ? ` • Claimed by ${activeInterventionCase.claimedByEmail}` : " • Unclaimed"}
                </div>
              ) : (
                <div className="nia-muted">
                  Flag follow-up has been suggested for this session, but no case is open yet. Claiming will create the same coach workflow used for flagged sessions.
                </div>
              )}
              {flagCaseStatus ? <div className="nia-muted">{flagCaseStatus}</div> : null}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowCoachPanel(true)}
                >
                  Coach messages
                </button>
                {canClaimCase ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={flagCaseBusy}
                    onClick={handleClaimFlagCase}
                  >
                    {flagCaseBusy ? "Working..." : "Claim case"}
                  </button>
                ) : null}
                <select
                  value={resolutionType}
                  disabled={flagCaseBusy || !canResolveCase}
                  onChange={(e) => setResolutionType(String(e.target.value || "resolved"))}
                >
                  <option value="resolved">Resolved</option>
                  <option value="intervened">Intervened</option>
                  <option value="no_intervention_needed">No intervention needed</option>
                  <option value="false_positive">False positive</option>
                </select>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={flagCaseBusy || !canResolveCase}
                  onClick={() => handleResolveFlagCase(resolutionType)}
                >
                  {flagCaseBusy ? "Working..." : "Mark resolved"}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={flagCaseBusy || !canResolveCase}
                  onClick={() => handleResolveFlagCase("no_intervention_needed")}
                >
                  {flagCaseBusy ? "Working..." : "No intervention needed"}
                </button>
              </div>
            </div>
          </Card>
        ) : null}

        {canSeeMemberSafeSummary && messagesForView.length > 1 ? (
          <Card className="chat-details-card chat-details-card--summary" title={canUseCoachTools ? "Session Toolkit" : "Session Summary"}>
            <div style={{ display: "grid", gap: 12 }}>
              <div className="ui-card chat-details-subcard chat-details-subcard--summary" style={{ padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div>
                    <strong>Session Summary</strong>
                    <div className="nia-muted" style={{ marginTop: 4 }}>
                      {summaryGenerationLabel}
                      {sessionSummaryDetailsVisible && visibleSessionSummary.generatedAt
                        ? ` • ${new Date(visibleSessionSummary.generatedAt).toLocaleString()}`
                        : ""}
                      {finalizationDetailsVisible &&
                      continuationView.source === "legacy" &&
                      getSummaryPersistenceLabel(visibleSessionSummary)
                        ? ` • ${getSummaryPersistenceLabel(visibleSessionSummary)}`
                        : ""}
                    </div>
                  </div>
                  {sessionSummaryDetailsVisible && canUseCoachTools && actionableFlagSuggested
                    ? <span className="nia-pill">Follow-up suggested</span>
                    : null}
                </div>
                <div className="chat-details-summary-text" style={{ marginTop: 10 }}>{maskedSessionSummaryText}</div>
                {activeSummaryNotice ? (
                  <div className="nia-muted" style={{ marginTop: 4 }}>
                    {activeSummaryNotice}
                  </div>
                ) : null}
                {(
                  sessionSummaryDetailsVisible &&
                  visibleSessionSummary.persistenceStatus !== "failed"
                ) ? (
                  <>
                    {visibleSessionSummary.detectedThemes.length ? (
                      <div className="nia-muted" style={{ marginTop: 8 }}>
                        Themes: {visibleSessionSummary.detectedThemes.join(", ")}
                      </div>
                    ) : null}
                    {memberSafeSuggestedNextStep ? (
                      <div className="nia-muted" style={{ marginTop: 4 }}>
                        Next step: {memberSafeSuggestedNextStep}
                      </div>
                    ) : null}
                  </>
                ) : null}
                {sessionSummaryDetailsVisible &&
                visibleSessionSummary.persistenceStatus === "failed" ? (
                  <div className="nia-muted" style={{ marginTop: 4 }}>
                    {visibleSessionSummary.persistenceError ||
                      "The generated summary is visible locally but was not saved to the shared session record."}
                  </div>
                ) : null}
                {sessionCompletion.sessionComplete === true ? (
                  <div style={{ marginTop: 10, display: "grid", gap: 4 }}>
                    <div><strong>Automatic email:</strong> {summaryDeliveryLabel}</div>
                    <div><strong>Manual resend:</strong> {summaryManualResendLabel}</div>
                    <div><strong>Reminder:</strong> {summaryReminderLabel}</div>
                  </div>
                ) : null}
              </div>

              {displayedFollowUp ? (
                <SessionFollowUpPanel
                  followUp={displayedFollowUp}
                  audience={sessionAudience}
                  canManage={canUseCoachTools}
                  readOnly={isReadOnly}
                  onChange={applyFollowUpPatch}
                />
              ) : null}

              {displayedFollowUpRecommendation?.active ? (
                <div className="ui-card chat-details-subcard" style={{ padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div>
                      <strong>Recommended by Nia</strong>
                      <div className="nia-muted" style={{ marginTop: 4 }}>
                        Suggested next session
                      </div>
                    </div>
                    <span className="nia-pill">Advisory</span>
                  </div>
                  <div style={{ marginTop: 10 }}>{memberSafeRecommendationText}</div>
                  {displayedFollowUpRecommendation.rationale ? (
                    <div className="nia-muted" style={{ marginTop: 8 }}>
                      {displayedFollowUpRecommendation.rationale}
                    </div>
                  ) : null}
                  {canUseCoachTools ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                      <button className="btn btn-ghost" onClick={() => setShowSchedule(true)} disabled={isReadOnly}>
                        Schedule follow-up
                      </button>
                      <button className="btn btn-ghost" onClick={dismissFollowUpRecommendation} disabled={isReadOnly}>
                        Dismiss recommendation
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {canUseCoachTools ? (
                <div className="ui-card chat-details-subcard" style={{ padding: 12 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="btn btn-ghost" onClick={() => setShowSchedule(true)} disabled={isReadOnly || messagesForView.length <= 1}>
                      Schedule follow-up
                    </button>
                    {sessionCompletion.sessionComplete === true ? (
                      <button
                        className="btn btn-ghost"
                        onClick={() => setShowEmail(true)}
                        disabled={
                          isReadOnly ||
                          messagesForView.length <= 1 ||
                          continuationView.finalization.status !== "ready" ||
                          continuationView.provenance?.automaticEmailBindingValid !== true ||
                          !summaryManualResendEligibility.eligible
                        }
                      >
                        Email summary
                      </button>
                    ) : null}
                    <button className="btn btn-ghost" onClick={() => setShowCoachPanel(true)}>
                      Coach messages
                      {coachUnread > 0 ? ` (${coachUnread})` : ""}
                    </button>
                  </div>
                </div>
              ) : null}

              {canUseCoachTools && (
                <>
                  <div className="ui-card chat-details-subcard" style={{ padding: 12 }}>
                    <strong>Session Attachments</strong>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                      <input
                        type="file"
                        accept=".pdf,.txt,.json,.csv,.png,.jpg,.jpeg"
                        disabled={isReadOnly}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (!f) return;
                          const reader = new FileReader();
                          reader.onload = () => {
                            const item = { name: f.name, type: f.type, dataUrl: reader.result, ts: Date.now() };
                            setAttachments((prev) => [item, ...prev]);
                          };
                          reader.readAsDataURL(f);
                        }}
                      />
                    </div>
                  </div>

                  <CoachNotesPanel notes={notes} setNotes={setNotes} readOnly={isReadOnly} />

                  <div className="ui-card chat-details-subcard" style={{ padding: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong>12-month Plan (lite)</strong>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setAssignments((a) => [...a, { game: GAMES[0], cadence: "Weekly", start: "", end: "" }])}
                        disabled={isReadOnly}
                      >
                        + Add item
                      </button>
                    </div>
                    {assignments.length === 0 ? (
                      <div style={{ marginTop: 8, color: "var(--ui-muted)" }}>No assignments yet.</div>
                    ) : (
                      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                        {assignments.map((row, i) => (
                          <div key={i} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr auto", gap: 8 }}>
                            <select
                              value={row.game}
                              onChange={(e) =>
                                setAssignments((a) => a.map((r, idx) => (idx === i ? { ...r, game: e.target.value } : r)))
                              }
                              disabled={isReadOnly}
                            >
                              {GAMES.map((g) => (
                                <option key={g} value={g}>
                                  {g}
                                </option>
                              ))}
                            </select>
                            <select
                              value={row.cadence}
                              onChange={(e) =>
                                setAssignments((a) => a.map((r, idx) => (idx === i ? { ...r, cadence: e.target.value } : r)))
                              }
                              disabled={isReadOnly}
                            >
                              <option>Weekly</option>
                              <option>Fortnightly</option>
                              <option>Monthly</option>
                              <option>Daily</option>
                            </select>
                            <input
                              type="date"
                              value={row.start}
                              onChange={(e) =>
                                setAssignments((a) => a.map((r, idx) => (idx === i ? { ...r, start: e.target.value } : r)))
                              }
                              disabled={isReadOnly}
                            />
                            <input
                              type="date"
                              value={row.end}
                              onChange={(e) =>
                                setAssignments((a) => a.map((r, idx) => (idx === i ? { ...r, end: e.target.value } : r)))
                              }
                              disabled={isReadOnly}
                            />
                            <button
                              className="btn btn-ghost"
                              onClick={() => setAssignments((a) => a.filter((_, idx) => idx !== i))}
                              disabled={isReadOnly}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>
        ) : null}
      </ChatDetailsPanel>

      {/* Modals */}
      {canUseCoachTools && (
        <>
          <ScheduleFollowUpModal
            open={showSchedule}
            onBeforeSubmit={(form) => {
              applyFollowUpPatch({
                status: "scheduled",
                scheduledFor: toScheduledFollowUpIso(form.preferred_date, form.preferred_time),
              }, "coach");
            }}
            onClose={(ok) => {
              setShowSchedule(false);
              if (ok) alert("Follow-up saved.");
            }}
            defaultName={displayName}
            defaultEmail={user?.email || ""}
            defaultDate={toFollowUpDateInputValue(displayedFollowUp?.scheduledFor)}
            defaultTime={toFollowUpTimeInputValue(displayedFollowUp?.scheduledFor)}
          />
          <EmailSummaryModal
            open={showEmail}
            onClose={() => {
              setShowEmail(false);
            }}
            orgId={activeOrgId || user?.orgId || "org_default"}
            sessionId={sessionId || sessionStartRef.current || ""}
            summaryGenerationStatus={
              visibleSessionSummary.generationStatus ||
              visibleSessionSummary.status ||
              (visibleSessionSummary.fallbackUsed ? "draft" : "final")
            }
            summaryPersistenceStatus={visibleSessionSummary.persistenceStatus || ""}
            initialDelivery={normalizeSummaryDelivery(
              activePostSessionRecord?.summaryDelivery || summaryDelivery
            )}
            postSessionContinuation={
              activePostSessionRecord?.postSessionContinuation ||
              postSessionContinuation
            }
            sessionRecord={activePostSessionRecord}
            draftSummary={visibleSessionSummary.summaryText || ""}
          />
        </>
      )}

      {showCoachPanel && (
        <div className="nia-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="coach-title">
          <div className="nia-modal coach-modal">
            <div className="nia-modal-header">
              <h2 id="coach-title" style={{ margin: 0 }}>{coachMessagingTitle}</h2>
              <p style={{ margin: "6px 0 0", color: "var(--muted)" }}>
                {coachMessagingIntro}
              </p>
            </div>
            <div className="nia-modal-body">
              {sessionId ? (
                <>
                  <div className="chat-box coach-box">
                    {activeCoachThread.length ? (
                      activeCoachThread.map((msg, i) => {
                        const label = getCoachThreadSenderLabel(msg, sessionViewRecord, role);
                        return (
                          <div key={`${msg.createdAt || i}-${i}`} className={`chat-row ${label === "Coach" ? "assistant" : "user"}`}>
                            <div className={`bubble ${label === "Coach" ? "assistant" : "user"}`}>
                              <strong>{label}:</strong> {msg.text}
                              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                                {msg.createdAt ? new Date(msg.createdAt).toLocaleString() : ""}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="nia-muted">No coach messages yet.</div>
                    )}
                  </div>
                  <textarea
                    rows="2"
                    className="chat-input"
                    value={coachInput}
                    disabled={coachSending || isReadOnly}
                    onChange={(e) => setCoachInput(e.target.value)}
                    placeholder={coachMessagingPlaceholder}
                    style={{ marginTop: 8 }}
                  />
                </>
              ) : (
                <div className="nia-muted" style={{ display: "grid", gap: 10 }}>
                  <span>Open a session to enable coach messaging.</span>
                  {sessions.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => {
                        const rec = sessions[0];
                        if (!rec) return;
                        applySessionRecord(rec);
                      }}
                    >
                      Open latest session
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="nia-modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowCoachPanel(false)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                disabled={isReadOnly || coachSending || !coachInput.trim() || !sessionId}
                onClick={handleCoachSend}
              >
                {coachSending ? "Sending…" : "Send message"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRating && (
        <div className="nia-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rating-title">
          <div className="nia-modal">
            <div className="nia-modal-header">
              <h2 id="rating-title" style={{ margin: 0 }}>How was this session?</h2>
              <p style={{ margin: "6px 0 0", color: "var(--muted)" }}>
                Your feedback helps us improve Nia during launch.
              </p>
            </div>
            <div className="nia-modal-body">
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    className={`btn btn-ghost${ratingScore === n ? " selected" : ""}`}
                    onClick={() => setRatingScore(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <textarea
                rows="3"
                className="chat-input"
                placeholder="Optional feedback..."
                value={ratingFeedback}
                onChange={(e) => setRatingFeedback(e.target.value)}
              />
            </div>
            <div className="nia-modal-footer">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowRating(false);
                  setRatingDismissed(true);
                  try {
                    if (ratingDismissKey) localStorage.setItem(ratingDismissKey, "1");
                  } catch {}
                }}
              >
                Maybe later
              </button>
              <button className="btn btn-primary" onClick={submitRating} disabled={!ratingScore}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
